import { Ajuda } from '../../design/Ajuda';
import { ComoFoiMeuGiro } from './MeuGiroNota';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import {
  bN,
  concluirContagem,
  contar,
  finalizar,
  fotoDaReposicao,
  fotoDoItem,
  iniciarReposicao,
  lerCodigo,
  lerMeuGiro,
  lerMissao,
  pularItem,
  pularNaReposicao,
  lerParaEditar,
  editarItem,
  lerReposicao,
  lerResumo,
  pausarMissao,
  primeiroNome,
  registrarOcorrencia,
  retomarMissao,
  repor,
  TIPOS_DE_OCORRENCIA,
  TIPOS_DE_PAUSA,
} from '../../giro';
import type {
  ItemParaEditar,
  Escrita,
  ItemDaLista,
  ItemDaReposicao,
  MeuBloco,
  MeuGiro,
  MissaoDoBalconista,
  ResultadoDaContagem,
  ResultadoDaLeitura,
  ResultadoDaReposicao,
  ResumoDaContagem,
  TipoDeOcorrencia,
  TipoDePausa,
} from '../../giro';
import { enviarFoto, hora } from '../../vendas';
import {
  abrirCameraTraseira,
  ajustarCamera,
  criarDetector,
  focarDeNovo,
  lerQuadro,
  temCamera,
  usarLanterna,
  type Detector,
} from '../../leitor-de-codigo';
import { agoraCarimbo, carimbarFoto, type FonteDaFoto } from '../balconista/foto';
import { CameraDaFoto } from '../balconista/CameraDaFoto';
import {
  ATALHOS_DE_QUANTIDADE,
  codigoParaIdentificar,
  depoisDeContar,
  itemDoCodigoLido,
  jaEstaNoInicio,
  linhaDoProduto,
  podeEnviarLeitura,
  proximoPassoDaContagem,
  resumoDaReposicao,
} from './regras-do-balconista';
import type { PassoDaContagem, UltimaLeitura } from './regras-do-balconista';
import './giro-balconista.css';

/**
 * TELAS 19, 20 e 21 — O GIRO DO BALCONISTA. "Giro primeiro. InovaPay depois."
 *
 *   19 Missão     o gate: shell focado, sem bottom nav, com o progresso Conferência → Reposição → InovaPay
 *   20 Contagem   identificar (digitar, tocar na lista ou "Escanear código") → contar → confirmar →
 *                 PRÓXIMO PRODUTO DIRETO (Carlos, 29/09: sem tela "Repor X" no meio, câmera só
 *                 por toque). A reposição continua calculada no servidor: aparece no resumo/fila.
 *   21 Reposição  resumo → fila → "achei tudo / uma parte / nenhuma" → fim do Giro
 *
 * Quem decide EAN, estoque, gate e liberação é o servidor. Escrita sem resposta é INCERTA — a
 * tela diz "não enviado", com repetir, e nunca finge que salvou.
 */

const dataDoCabecalho = (dia: string) => {
  const t = new Date(`${dia}T12:00:00Z`).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  const [semana, resto] = t.split(', ');
  return `${(semana ?? '').replace('-feira', '').replace(/^./, (c) => c.toUpperCase())}, ${resto ?? ''}`;
};

type Tela =
  | { t: 'inicio' }
  | { t: 'lista'; missaoId: string }
  | { t: 'scanner'; missaoId: string; itemId: string }
  /** A lista de produtos do bloco: tocar abre a quantidade; "Escanear código" é opcional. */
  | { t: 'produtos'; missaoId: string; itemId: string }
  | { t: 'contar'; missaoId: string; itemId: string; entrada: 'SCAN' | 'MANUAL' }
  | { t: 'foto'; missaoId: string; itemId: string }
  | { t: 'resumo'; missaoId: string }
  | { t: 'fila'; missaoId: string }
  | { t: 'repor'; missaoId: string; itemId: string }
  | { t: 'diferenca'; missaoId: string; r: ResultadoDaReposicao; nome: string }
  /** Editar o Giro de hoje (dono, 13/10): volta para a tela de onde veio. */
  | { t: 'editar'; missaoId: string; volta: 'resumo' | 'fila' }
  | {
      t: 'fim';
      missaoId: string;
      resumo: ResumoDaContagem;
      repostos: number;
      unidades: number;
      diferencas: number;
      /** A resposta do servidor ao finalizar: só ela diz se o InovaPay abriu. */
      liberado: boolean;
    };

/**
 * Onde abre quem entra no bloco (iniciar ou retomar): a LISTA de produtos (dono, 29/09) — nunca
 * a câmera. Foto que ficou para trás e resumo continuam tendo a vez deles.
 */
function telaDeEntrada(missaoId: string, p: PassoDaContagem): Tela {
  if (p.t === 'resumo') return { t: 'resumo', missaoId };
  if (p.t === 'foto') return { t: 'foto', missaoId, itemId: p.itemId };
  return { t: 'produtos', missaoId, itemId: p.itemId };
}

/** Link para o InovaPay que nunca fica morto (ver `jaEstaNoInicio`). */
function entrarNoInovaPay(e: { preventDefault: () => void }) {
  e.preventDefault();
  if (jaEstaNoInicio(window.location.hash)) window.location.reload();
  else window.location.hash = '#/inicio';
}

/** Falha ao LER (não é escrita): diz o que houve, tenta de novo ou volta — nunca esqueleto eterno. */
function FalhaAoCarregar({
  oQue,
  aoTentar,
  aoVoltar,
}: {
  oQue: string;
  aoTentar: () => void;
  aoVoltar: () => void;
}) {
  return (
    <div className="gb-cartao gb-erro" role="alert">
      <b>Não foi possível carregar {oQue}</b>
      <p>Confira a conexão e tente de novo. Nada do que você já registrou se perdeu.</p>
      <button type="button" className="gb-botao" onClick={aoTentar}>
        <Icone nome="retry" tamanho={15} traco={1.8} /> Tentar de novo
      </button>
      <button type="button" className="gb-botao gb-botao-linha" onClick={aoVoltar}>
        <Icone nome="arrow-l" tamanho={15} traco={1.8} /> Voltar
      </button>
    </div>
  );
}

export function GiroDoBalconista({
  sessao,
  eu,
  aoSair,
}: {
  sessao: Sessao;
  eu: Eu | null;
  aoSair: () => void;
}) {
  const [g, setG] = useState<MeuGiro | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [tela, setTela] = useState<Tela>({ t: 'inicio' });
  const [menu, setMenu] = useState(false);
  const carregar = useCallback(() => {
    setFalhou(false);
    void lerMeuGiro().then((r) => {
      if (r.ok) setG(r.dados);
      else setFalhou(true);
    });
  }, []);
  useEffect(carregar, [carregar]);
  const voltar = () => {
    setTela({ t: 'inicio' });
    carregar();
  };
  /**
   * Troca de tela. Ao chegar no fim, o Giro em mãos é de ANTES de finalizar (ciclo, blocos): some
   * e é lido de novo, para a tela final não mostrar número velho.
   */
  const ir = (t: Tela) => {
    setTela(t);
    if (t.t === 'fim') {
      setG(null);
      carregar();
    }
  };
  /** Abre o bloco onde parou: próximo produto pendente, foto que faltou, resumo ou reposição. */
  const abrir = (b: MeuBloco) => {
    if (b.fase === 'reposicao') {
      setTela(
        b.estado === 'COUNTING_DONE'
          ? { t: 'resumo', missaoId: b.missaoId }
          : { t: 'fila', missaoId: b.missaoId },
      );
      return;
    }
    void lerMissao(b.missaoId).then((r) => {
      if (!r.ok) return setFalhou(true);
      setTela(telaDeEntrada(b.missaoId, proximoPassoDaContagem(r.dados.itens)));
    });
  };
  const nome = primeiroNome(eu?.nomeExibicao ?? g?.nome ?? '');

  if (tela.t !== 'inicio' && tela.t !== 'lista' && tela.t !== 'fim')
    return (
      <div className="gb gb-foco">
        <FluxoDaMissao
          sessao={sessao}
          tela={tela}
          ir={ir}
          aoVoltar={voltar}
          nome={eu?.nomeExibicao ?? ''}
        />
      </div>
    );

  const bloco =
    g?.blocos.find((b) => ['nao_iniciado', 'contagem', 'reposicao'].includes(b.fase)) ??
    g?.blocos[0];
  const etapa =
    !bloco || bloco.fase === 'nao_iniciado' || bloco.fase === 'contagem'
      ? 0
      : bloco.fase === 'reposicao'
        ? 1
        : 2;
  return (
    <div className="gb">
      <header className="gb-cab">
        <span className="gb-marca" aria-hidden="true">
          +
        </span>
        <div>
          <b>Giro de Prateleira</b>
          <em>{g ? dataDoCabecalho(g.dia) : ' '}</em>
        </div>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="gb-menu"
          aria-label="Menu"
          onClick={() => setMenu((m) => !m)}
        >
          <Icone nome="dots" tamanho={16} traco={1.8} />
        </button>
        {menu && (
          <div className="gb-menu-lista" role="menu">
            <a href="#/conta" role="menuitem">
              Ajuda
            </a>
            <button type="button" role="menuitem" onClick={aoSair}>
              Sair
            </button>
          </div>
        )}
      </header>
      <main className="gb-corpo">
        {falhou ? (
          <div className="gb-cartao gb-erro">
            <b>Não foi possível carregar o seu Giro</b>
            <p>Confira a conexão e tente de novo.</p>
            <button type="button" className="gb-botao gb-botao-linha" onClick={carregar}>
              <Icone nome="retry" tamanho={15} traco={1.8} /> Tentar novamente
            </button>
          </div>
        ) : !g ? (
          <div className="gb-esqueleto" aria-busy="true">
            <i />
            <i />
            <i />
          </div>
        ) : tela.t === 'fim' ? (
          <GiroConcluido
            tela={tela}
            bloco={g.blocos.find((b) => b.missaoId === tela.missaoId)}
            ciclo={g.ciclos[0]}
            aoVoltar={voltar}
          />
        ) : (
          <Gate
            g={g}
            nome={nome}
            ir={ir}
            abrir={abrir}
            tablet={tela.t === 'lista' ? tela.missaoId : null}
          />
        )}
      </main>
      {g && g.blocos.length > 0 && g.situacao !== 'cancelado' && (
        <footer className="gb-rodape">
          <ol className="gb-trilha">
            {['Conferência', 'Reposição', 'InovaPay'].map((x, i) => (
              <li
                key={x}
                className={
                  i < etapa || (i === 2 && g.liberado)
                    ? 'gb-trilha-feito'
                    : i === etapa && !g.liberado
                      ? 'gb-trilha-atual'
                      : ''
                }
              >
                {i < etapa || (i === 2 && g.liberado) ? (
                  <Icone nome="check" tamanho={11} traco={2.4} />
                ) : (
                  <i />
                )}
                {x}
              </li>
            ))}
          </ol>
          <div className="gb-rodape-links">
            <a href="#/conta">Ajuda</a>
            <button type="button" onClick={aoSair}>
              Sair
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

/* ================================================================== *
 * 19 — o gate
 * ================================================================== */

function Gate({
  g,
  nome,
  ir,
  abrir,
  tablet,
}: {
  g: MeuGiro;
  nome: string;
  ir: (t: Tela) => void;
  abrir: (b: MeuBloco) => void;
  tablet: string | null;
}) {
  const ativos = g.blocos.filter((b) => b.fase !== 'cancelado');
  const cancelado = g.blocos.find((b) => b.cancelamento);
  const transferido = g.transferidosDeMim[0];
  if (g.situacao === 'cancelado' && cancelado?.cancelamento)
    return (
      <div className="gb-cartao gb-cartao-alerta">
        <div className="gb-faixa gb-faixa-vermelha">
          <Icone nome="warn" tamanho={16} traco={1.8} />
          <div>
            <b>Operação encerrada pelo administrador</b>
            <p>
              O Giro de hoje foi cancelado às {hora(cancelado.cancelamento.em)}. Novas contagens não
              são aceitas.
            </p>
          </div>
        </div>
        <p className="gb-pad">
          Seu progresso anterior —{' '}
          <b>{g.blocos.reduce((a, b) => a + b.contados, 0)} produtos conferidos</b> — permanece
          registrado.
        </p>
        <a className="gb-botao gb-botao-linha gb-m" href="#/inicio" onClick={entrarNoInovaPay}>
          Voltar ao InovaPay
        </a>
      </div>
    );
  if (!g.blocos.length && transferido)
    return (
      <div className="gb-cartao">
        <div className="gb-faixa gb-faixa-azul">
          <Icone nome="cycle" tamanho={16} traco={1.8} />
          <div>
            <b>Seu bloco foi transferido</b>
            <p>
              O {bN(transferido.numero)} passou para outra pessoa às {hora(transferido.em)}.
            </p>
          </div>
        </div>
        <p className="gb-pad">
          Os <b>{transferido.contados} itens que você conferiu</b> continuam no seu nome. Como você
          não tem mais pendência obrigatória, o servidor liberou suas atividades.
        </p>
        <a className="gb-botao gb-m" href="#/inicio" onClick={entrarNoInovaPay}>
          Entrar no InovaPay
        </a>
        <p className="gb-nota">
          O bloco não entra no ciclo de nenhum dos dois, porque ninguém o concluiu inteiro.
        </p>
      </div>
    );
  if (!g.blocos.length)
    return (
      <>
        <h1 className="gb-ola">Olá, {nome}</h1>
        {g.situacao === 'aguardando_publicacao' ? (
          <div className="gb-vazio">
            <span className="gb-vazio-icone gb-vazio-ambar">
              <Icone nome="clock" tamanho={18} traco={1.7} />
            </span>
            <b>O Giro de hoje ainda não foi publicado</b>
            <p>Assim que o administrador publicar, seu bloco aparece aqui.</p>
          </div>
        ) : (
          <div className="gb-vazio">
            <span className="gb-vazio-icone">
              <Icone nome="filter" tamanho={18} traco={1.7} />
            </span>
            <b>Nenhum bloco atribuído a você</b>
            <p>
              {g.setorSemMissao
                ? `Nesta operação seu setor tem ${String(g.setorSemMissao.blocos)} blocos e ${String(g.setorSemMissao.presentes)} pessoas presentes.`
                : 'Você não está em nenhuma operação de Giro hoje.'}
            </p>
          </div>
        )}
        <a className="gb-botao gb-botao-linha gb-m" href="#/inicio" onClick={entrarNoInovaPay}>
          Ir para o InovaPay
        </a>
      </>
    );

  // Horário do Giro (dono, 28/09): antes da hora ele vende normal e o Giro não começa.
  if (g.antesDoHorario && g.horario)
    return (
      <>
        <h1 className="gb-ola">Olá, {nome}</h1>
        <div className="gb-vazio">
          <span className="gb-vazio-icone gb-vazio-ambar">
            <Icone nome="clock" tamanho={18} traco={1.7} />
          </span>
          <b>Seu Giro de hoje começa às {g.horario}</b>
          <p>Até lá, siga vendendo normalmente no InovaPay. Às {g.horario} o Giro abre aqui.</p>
        </div>
        <a className="gb-botao gb-m" href="#/inicio" onClick={entrarNoInovaPay}>
          Ir para o InovaPay
        </a>
      </>
    );

  const dois = ativos.length > 1;
  const principal =
    ativos.find((b) => ['nao_iniciado', 'contagem', 'reposicao'].includes(b.fase)) ?? ativos[0];
  const recebido = ativos.find((b) => b.recebido);
  const inicial =
    principal &&
    principal.fase === 'nao_iniciado' &&
    principal.contados === 0 &&
    !dois &&
    !recebido;
  return (
    <div className={tablet ? 'gb-tablet' : ''}>
      <div className="gb-col">
        {recebido?.recebido && (
          // Só "Bloco recebido" (dono, 27/09/2026): sem o nome de quem passou nem o que já contou.
          <div className="gb-faixa gb-faixa-azul gb-faixa-solta">
            <Icone nome="cycle" tamanho={16} traco={1.8} />
            <div>
              <b>Bloco recebido</b>
              <p>O {bN(recebido.numero)} agora é seu.</p>
            </div>
          </div>
        )}
        {g.liberado &&
          !recebido &&
          ativos.every((b) => b.fase === 'concluido' || b.fase === 'aguardando_sistema') && (
            <div className="gb-faixa gb-faixa-verde gb-faixa-solta">
              <Icone nome="check" tamanho={16} traco={2} />
              <div>
                <b>Seu Giro de hoje está concluído</b>
                <p>As atividades do InovaPay estão liberadas.</p>
              </div>
            </div>
          )}
        {inicial && (
          <div className="gb-faixa gb-faixa-verde gb-faixa-solta">
            <Icone nome="check" tamanho={16} traco={2} />
            <b>Seu Giro de hoje está disponível.</b>
          </div>
        )}
        <h1 className="gb-ola">Olá, {nome}</h1>
        <p className="gb-sub">
          {recebido
            ? `O ${bN(recebido.numero)} agora faz parte do seu Giro de hoje.`
            : dois
              ? `Você precisa concluir os ${String(ativos.length)} blocos atribuídos nesta operação. Eles são independentes.`
              : inicial
                ? 'Antes de iniciar suas atividades no InovaPay, conclua seu Giro de Prateleira de hoje.'
                : g.liberado
                  ? 'Obrigado. Seu bloco foi registrado.'
                  : 'Conclua o Giro para liberar as atividades do InovaPay.'}
        </p>
        {dois || recebido ? (
          ativos.map((b) => <CartaoPequeno key={b.missaoId} b={b} abrir={abrir} />)
        ) : principal ? (
          <CartaoGrande b={principal} ir={ir} abrir={abrir} inicial={!!inicial} />
        ) : null}
        {inicial && (
          <div className="gb-cartao gb-como">
            <b>Como funciona hoje</b>
            <ol>
              <li>
                <span>1</span>
                <div>
                  <b>Conferir os {principal?.total} produtos</b>
                  <p>Escanear, contar a prateleira, confirmar. Um produto por vez.</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <b>Repor o que a lista pedir</b>
                  <p>A lista sai pronta no final — você vai ao estoque uma vez só.</p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <b>Atividades do InovaPay liberadas</b>
                  <p>Vitaminas, pré-vencidos, paradão e vendas ficam disponíveis.</p>
                </div>
              </li>
            </ol>
          </div>
        )}
        {!dois && !inicial && principal && principal.fase === 'contagem' && (
          <div className="gb-cartao gb-etapa-fut">
            <Icone nome="box" tamanho={16} traco={1.6} />
            <div>
              <b>Reposição</b>
              <p>Começa quando você terminar a contagem</p>
            </div>
          </div>
        )}
        {dois && (
          <div className="gb-cartao gb-info">
            <Icone nome="filter" tamanho={15} traco={1.6} />
            <p>
              Hoje a equipe tem menos pessoas que blocos, então você ficou com {ativos.length}. As
              atividades do InovaPay serão liberadas depois da conclusão dos{' '}
              {ativos.length === 2 ? 'dois' : String(ativos.length)}.
            </p>
          </div>
        )}
        {g.ciclos[0] && (
          <div className="gb-cartao gb-ciclo">
            <div>
              <Icone nome="cycle" tamanho={15} traco={1.6} />
              <span>
                Seu ciclo em {g.ciclos[0].setor}
                <Ajuda rotulo="o ciclo">
                  Cada pessoa passa por todos os blocos do setor. A barra mostra quantos você já
                  terminou nesta volta. Só bloco terminado inteiro conta.
                </Ajuda>
                {recebido && <em>Um bloco só entra no ciclo se for concluído inteiro</em>}
              </span>
              <b>
                {g.ciclos[0].feitos} de {g.ciclos[0].total}
              </b>
            </div>
            {!recebido && (
              <div className="gb-ciclo-barras">
                {Array.from({ length: g.ciclos[0].total }, (_, i) => (
                  <i key={i} className={i < (g.ciclos[0]?.feitos ?? 0) ? 'gb-ciclo-ok' : ''} />
                ))}
              </div>
            )}
            {inicial && <p className="gb-nota">Um bloco entra no ciclo quando é concluído.</p>}
          </div>
        )}
        <ComoFoiMeuGiro />
        {g.liberado && (
          <a className="gb-botao gb-m" href="#/inicio" onClick={entrarNoInovaPay}>
            Entrar no InovaPay
          </a>
        )}
        <a className="gb-problema" href="#/conta">
          <Icone nome="pin" tamanho={13} traco={1.6} /> Informar problema
        </a>
      </div>
      {tablet && <ListaDoBloco missaoId={tablet} />}
    </div>
  );
}

function faixaDoCartao(b: MeuBloco) {
  return (
    <div className="gb-faixa-bloco">
      <Icone nome="filter" tamanho={15} traco={1.6} />
      <div>
        <em>
          Faixa desta operação
          <Ajuda rotulo="a faixa">
            Os produtos do seu bloco hoje: os nomes que começam entre essas letras (ou esses
            laboratórios). Amanhã a faixa pode mudar.
          </Ajuda>
        </em>
        <b>{b.faixa}</b>
      </div>
      <span style={{ flex: 1 }} />
      <div className="gb-dir">
        <em>Produtos</em>
        <b>{b.total}</b>
      </div>
    </div>
  );
}

function CartaoGrande({
  b,
  ir,
  abrir,
  inicial,
}: {
  b: MeuBloco;
  ir: (t: Tela) => void;
  abrir: (b: MeuBloco) => void;
  inicial: boolean;
}) {
  const restantes = b.total - b.contados;
  return (
    <div className="gb-cartao gb-bloco">
      <span className="gb-rotulo">SEU BLOCO DE HOJE</span>
      <h2>
        {b.setor} <span className="gb-numero">{bN(b.numero)}</span>
      </h2>
      {faixaDoCartao(b)}
      {inicial ? (
        <p className="gb-nota">
          A faixa vale para a operação de hoje — ela é recalculada em cada geração.
        </p>
      ) : (
        <>
          <div className="gb-prog">
            <span>{b.fase === 'reposicao' ? 'Reposição' : 'Contagem'}</span>
            <b>
              {b.fase === 'reposicao' ? b.repostos : b.contados}{' '}
              <em>de {b.fase === 'reposicao' ? b.aRepor : b.total}</em>
            </b>
          </div>
          <div className="gb-barra">
            <i
              style={{
                width: `${String(((b.fase === 'reposicao' ? b.repostos / Math.max(1, b.aRepor) : b.contados / Math.max(1, b.total)) * 100).toFixed(0))}%`,
              }}
            />
          </div>
          {b.fase === 'contagem' && (
            <div className="gb-prog gb-prog-peq">
              <span>{b.iniciadaEm ? `iniciado às ${hora(b.iniciadaEm)}` : ''}</span>
              <b className="gb-vermelho">{restantes} restantes</b>
            </div>
          )}
        </>
      )}
      {b.fase !== 'concluido' && b.fase !== 'aguardando_sistema' && (
        <>
          <button type="button" className="gb-botao" onClick={() => abrir(b)}>
            <Icone nome="cam" tamanho={17} traco={1.8} />
            {b.fase === 'reposicao'
              ? 'Continuar reposição'
              : b.contados
                ? 'Continuar contagem'
                : 'Iniciar contagem'}
          </button>
          {b.fase !== 'reposicao' && (
            <button
              type="button"
              className="gb-link"
              onClick={() => ir({ t: 'lista', missaoId: b.missaoId })}
            >
              Ver lista do bloco
            </button>
          )}
        </>
      )}
    </div>
  );
}

function CartaoPequeno({ b, abrir }: { b: MeuBloco; abrir: (b: MeuBloco) => void }) {
  const selo =
    b.recebido && b.fase === 'nao_iniciado'
      ? { r: 'Bloco recebido', c: 'gb-selo-azul' }
      : b.fase === 'contagem'
        ? { r: 'Em contagem', c: 'gb-selo-ambar' }
        : b.fase === 'reposicao'
          ? { r: 'Reposição', c: 'gb-selo-ambar' }
          : b.fase === 'concluido'
            ? { r: 'Concluído', c: 'gb-selo-verde' }
            : { r: 'Não iniciado', c: '' };
  return (
    <div className="gb-cartao gb-bloco gb-bloco-peq">
      <div className="gb-bloco-topo">
        <h3>
          {b.setor} <span className="gb-numero">{bN(b.numero)}</span>
        </h3>
        <span className={`gb-selo ${selo.c}`}>{selo.r}</span>
      </div>
      <div className="gb-tres">
        <div>
          <em>Faixa</em>
          <b>{b.faixa}</b>
        </div>
        <div>
          <em>Produtos</em>
          <b>{b.total}</b>
        </div>
        <div>
          <em>Conferidos</em>
          <b>{b.contados}</b>
        </div>
      </div>
      <div className="gb-barra">
        <i
          style={{ width: `${String(((b.contados / Math.max(1, b.total)) * 100).toFixed(0))}%` }}
        />
      </div>
      {b.fase !== 'concluido' && b.fase !== 'aguardando_sistema' && (
        <button
          type="button"
          className={`gb-botao ${b.fase === 'nao_iniciado' ? 'gb-botao-linha' : ''}`}
          onClick={() => abrir(b)}
        >
          {b.fase === 'nao_iniciado' ? `Iniciar ${bN(b.numero)}` : `Continuar ${bN(b.numero)}`}
        </button>
      )}
    </div>
  );
}

function ListaDoBloco({ missaoId }: { missaoId: string }) {
  const [m, setM] = useState<MissaoDoBalconista | null>(null);
  useEffect(() => {
    void lerMissao(missaoId).then((r) => r.ok && setM(r.dados));
  }, [missaoId]);
  const atual = m?.itens.find((i) => i.situacao === 'pendente');
  return (
    <div className="gb-cartao gb-lista">
      <b>Lista do bloco</b>
      <p className="gb-nota">Ordem física recebida do sistema · sem estoque antes da contagem</p>
      <ul>
        {m?.itens.map((i) => (
          <li key={i.id} className={i.id === atual?.id ? 'gb-lista-atual' : ''}>
            <IconeDaSituacao i={i} atual={i.id === atual?.id} />
            <div>
              <b>{i.nome}</b>
              <em className="gb-mono">{i.codigo ?? '—'}</em>
            </div>
            <SeloDaSituacao
              i={i}
              atual={i.id === atual?.id}
              posicao={`${String(i.ordem)} de ${String(m.itens.length)}`}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function IconeDaSituacao({ i, atual }: { i: ItemDaLista; atual: boolean }) {
  if (atual) return <span className="gb-circulo gb-circulo-atual" />;
  if (i.situacao === 'pendente') return <span className="gb-circulo" />;
  if (i.situacao === 'aguardando_estoque') return <Icone nome="clock" tamanho={15} traco={1.7} />;
  if (i.situacao === 'nao_localizado') return <Icone nome="pin" tamanho={15} traco={1.7} />;
  return <Icone nome="check" tamanho={15} traco={2} />;
}
function SeloDaSituacao({
  i,
  atual,
  posicao,
}: {
  i: ItemDaLista;
  atual: boolean;
  posicao: string;
}) {
  if (atual) return <span className="gb-selo gb-selo-vermelho">Atual · {posicao}</span>;
  if (i.situacao === 'pendente') return <span className="gb-nota">pendente</span>;
  if (i.situacao === 'aguardando_estoque')
    return <span className="gb-selo gb-selo-ambar">Aguardando estoque</span>;
  if (i.situacao === 'nao_localizado') return <span className="gb-selo">Não localizado</span>;
  if (i.fotoEnviada) return <span className="gb-selo gb-selo-ambar">Foto enviada</span>;
  if (i.situacao === 'foto_pendente')
    return <span className="gb-selo gb-selo-vermelho">Foto pendente</span>;
  return <span className="gb-nota">{i.contadoEm ? `contado às ${hora(i.contadoEm)}` : ''}</span>;
}

/* ================================================================== *
 * 20 e 21 — o fluxo da missão
 * ================================================================== */

function FluxoDaMissao({
  sessao,
  tela,
  ir,
  aoVoltar,
  nome,
}: {
  sessao: Sessao;
  tela: Exclude<Tela, { t: 'inicio' } | { t: 'lista' } | { t: 'fim' }>;
  ir: (t: Tela) => void;
  aoVoltar: () => void;
  nome: string;
}) {
  const [m, setM] = useState<MissaoDoBalconista | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [pausando, setPausando] = useState(false);
  const [salvoAviso, setSalvoAviso] = useState<{
    chave: number;
    nome: string;
    texto: string;
  } | null>(null);
  const [avisoDaLista, setAvisoDaLista] = useState<string | null>(null);
  /**
   * Itens que o servidor JÁ confirmou nesta tela. Quem conta rápido pode confirmar o próximo antes
   * de a lista recarregar: sem isto a lista velha mandava de volta a um produto já contado.
   */
  const resolvidos = useRef(new Set<string>());
  // O "Contagem salva" some sozinho: não bloqueia, o próximo produto já está na tela.
  useEffect(() => {
    if (!salvoAviso) return;
    const t = setTimeout(() => setSalvoAviso(null), 2200);
    return () => clearTimeout(t);
  }, [salvoAviso]);
  const recarregar = useCallback(() => {
    setFalhou(false);
    void lerMissao(tela.missaoId).then((r) => {
      if (r.ok) setM(r.dados);
      else setFalhou(true);
    });
  }, [tela.missaoId]);
  useEffect(recarregar, [recarregar]);
  if (!m)
    return falhou ? (
      <div className="gb-corpo">
        <FalhaAoCarregar oQue="o seu bloco" aoTentar={recarregar} aoVoltar={aoVoltar} />
      </div>
    ) : (
      <div className="gb-esqueleto" aria-busy="true">
        <i />
        <i />
      </div>
    );
  // Em pausa, a missão fica atrás da tela de pausa: continuar exige "Retomar" (o tempo parado
  // não entra no tempo ativo — §13 do 1.0).
  if (m.pausa) return <EmPausa sessao={sessao} m={m} aoVoltar={aoVoltar} aoRetomar={recarregar} />;
  if (pausando)
    return (
      <Pausar
        sessao={sessao}
        missaoId={m.missaoId}
        aoCancelar={() => setPausando(false)}
        aoPausar={() => {
          setPausando(false);
          recarregar();
        }}
      />
    );
  const itens = m.itens.map((i) =>
    i.situacao === 'pendente' && resolvidos.current.has(i.id)
      ? { ...i, situacao: 'contado' as const }
      : i,
  );
  const item = 'itemId' in tela ? itens.find((i) => i.id === tela.itemId) : undefined;
  const conferidos = itens.filter(
    (i) => i.situacao !== 'pendente' && i.situacao !== 'transferido',
  ).length;
  const total = itens.filter((i) => i.situacao !== 'transferido' || i.contadoEm).length;
  const proximo = () => {
    const passo = proximoPassoDaContagem(itens, 'itemId' in tela ? tela.itemId : null);
    recarregar();
    seguir(passo);
  };
  /**
   * PULAR PRODUTO (dono, 01/10): o servidor manda o item para o fim da fila; a lista é relida e
   * o próximo pendente abre. Quando só sobrou o pulado, o servidor recusa ("é o último") e a
   * tela mostra o motivo — ele conta e termina.
   */
  const pular = async (itemId: string, nomeDoItem: string): Promise<string | null> => {
    const r = await pularItem(sessao, itemId);
    if (r.tipo !== 'confirmada') return textoDaFalha(r);
    setSalvoAviso({ chave: Date.now(), nome: nomeDoItem, texto: 'foi para o fim da fila' });
    const l = await lerMissao(m.missaoId);
    if (!l.ok) {
      recarregar();
      return null;
    }
    setM(l.dados);
    seguir(proximoPassoDaContagem(l.dados.itens, null));
    return null;
  };
  /**
   * Abre a quantidade de um produto. No setor que exige EAN o servidor só aceita contagem de
   * produto identificado: o toque identifica como ENTRADA MANUAL (dono, 29/09). Falhou: volta à
   * lista com o motivo — nunca conta sem o servidor ter confirmado.
   */
  const abrirContagem = async (itemId: string): Promise<string | null> => {
    const alvo = itens.find((i) => i.id === itemId);
    if (!alvo) return 'Produto não encontrado neste bloco.';
    if (m.exigeEan) {
      const codigo = codigoParaIdentificar(alvo);
      if (!codigo) return 'Este produto não tem código cadastrado: use "Escanear código".';
      const r = await lerCodigo(sessao, itemId, codigo, 'MANUAL');
      if (r.tipo !== 'confirmada') return textoDaFalha(r);
      if (r.dados.resultado !== 'confirmado')
        return 'O sistema não confirmou este produto. Escaneie ou digite o código da caixa.';
    }
    ir({ t: 'contar', missaoId: m.missaoId, itemId, entrada: 'MANUAL' });
    return null;
  };
  /** O passo seguinte: produto → quantidade direto; foto sorteada; ou o resumo (reposição). */
  const seguir = (passo: PassoDaContagem) => {
    if (passo.t === 'resumo') return ir({ t: 'resumo', missaoId: m.missaoId });
    if (passo.t === 'foto') return ir({ t: 'foto', missaoId: m.missaoId, itemId: passo.itemId });
    void abrirContagem(passo.itemId).then((falha) => {
      if (falha) {
        setAvisoDaLista(falha);
        ir({ t: 'produtos', missaoId: m.missaoId, itemId: passo.itemId });
      }
    });
  };
  /** Contagem CONFIRMADA pelo servidor: aviso curto (não bloqueia) e o próximo produto já abre. */
  const salvo = (nomeDoItem: string, r: ResultadoDaContagem) => {
    setSalvoAviso({
      chave: Date.now(),
      nome: nomeDoItem,
      texto: r.naoLocalizado
        ? 'registrado como não localizado'
        : `${String(r.prateleira)} ${r.prateleira === 1 ? 'unidade confirmada' : 'unidades confirmadas'}`,
    });
    resolvidos.current.add(r.itemId);
    recarregar();
    seguir(depoisDeContar(itens, r.itemId, r));
  };
  const cabecalho = (
    titulo: string | null,
    sub: string,
    pct: number,
    escuro = false,
    aoVoltarDaqui: () => void = aoVoltar,
  ) => (
    <header className={`gb-fluxo-cab ${escuro ? 'gb-escuro' : ''}`}>
      <button type="button" className="gb-voltar" aria-label="Voltar" onClick={aoVoltarDaqui}>
        <Icone nome="arrow-l" tamanho={18} traco={1.8} />
      </button>
      <div>
        {titulo ? (
          <b>{titulo}</b>
        ) : (
          <span className="gb-mono">{`${m.setor} · ${bN(m.numero)} · ${m.faixa}`}</span>
        )}
        <em>
          {sub}
          {m.metaMin !== null && m.ativoMin !== null && (
            <span className={m.ativoMin > m.metaMin ? 'gb-tempo-estourou' : 'gb-tempo'}>
              {' · '}
              {m.ativoMin} de {m.metaMin} min
            </span>
          )}
        </em>
      </div>
      <span style={{ flex: 1 }} />
      {!escuro && <b className="gb-pct">{pct}%</b>}
      <button
        type="button"
        className="gb-voltar gb-pausar"
        aria-label="Pausar o Giro"
        title="Pausar"
        onClick={() => setPausando(true)}
      >
        <Icone nome="clock" tamanho={17} traco={1.8} />
      </button>
      <div className="gb-fluxo-barra">
        <i style={{ width: `${String(pct)}%` }} />
      </div>
    </header>
  );
  const pctCont = Math.round((conferidos / Math.max(1, total)) * 100);
  const posicao = item ? `Produto ${String(item.ordem)} de ${String(m.itens.length)}` : '';
  const irParaLista = (itemId: string) => ir({ t: 'produtos', missaoId: m.missaoId, itemId });
  const aviso = salvoAviso && (
    <div key={salvoAviso.chave} className="gb-toast" role="status" aria-live="polite">
      <Icone nome="check" tamanho={16} traco={2.2} />
      <div>
        <b>Contagem salva</b>
        <em>
          {salvoAviso.nome} · {salvoAviso.texto}
        </em>
      </div>
    </div>
  );

  switch (tela.t) {
    case 'scanner':
      return (
        <>
          {cabecalho(null, posicao, pctCont, true, () => irParaLista(tela.itemId))}
          <Scanner
            key={tela.itemId}
            sessao={sessao}
            itemId={tela.itemId}
            itens={itens}
            aoConfirmar={(itemId, entrada) =>
              ir({ t: 'contar', missaoId: m.missaoId, itemId, entrada })
            }
            aoManual={() => irParaLista(tela.itemId)}
            aoNaoAchei={(r) => salvo(item?.nome ?? '', r)}
            esperado={item}
          />
        </>
      );
    case 'produtos':
      return (
        <>
          {cabecalho(null, posicao, pctCont)}
          {aviso}
          <ListaDeProdutos
            key={m.missaoId}
            sessao={sessao}
            itemId={tela.itemId}
            itens={itens}
            avisoInicial={avisoDaLista}
            aoLimparAviso={() => setAvisoDaLista(null)}
            aoAbrir={abrirContagem}
            aoIdentificado={(itemId, entrada) =>
              ir({ t: 'contar', missaoId: m.missaoId, itemId, entrada })
            }
            aoEscanear={(itemId) => ir({ t: 'scanner', missaoId: m.missaoId, itemId })}
          />
        </>
      );
    case 'contar':
      return (
        <>
          {cabecalho(null, posicao, pctCont, false, () => irParaLista(tela.itemId))}
          {aviso}
          {item && (
            <Contagem
              key={item.id}
              sessao={sessao}
              missaoId={m.missaoId}
              item={item}
              itens={itens}
              entrada={tela.entrada}
              aoSalvar={(r) => salvo(item.nome, r)}
              aoPular={() => pular(item.id, item.nome)}
            />
          )}
        </>
      );
    case 'foto':
      return (
        <>
          {cabecalho(null, posicao, pctCont)}
          {item ? (
            <FotoQueFaltou sessao={sessao} item={item} nome={nome} aoProximo={proximo} />
          ) : (
            <div className="gb-corpo">
              <FalhaAoCarregar oQue="este produto" aoTentar={recarregar} aoVoltar={aoVoltar} />
            </div>
          )}
        </>
      );
    case 'resumo':
      return <ResumoDaContagemTela sessao={sessao} m={m} aoVoltar={aoVoltar} ir={ir} />;
    case 'fila':
      return <FilaDeReposicao sessao={sessao} m={m} aoVoltar={aoVoltar} ir={ir} />;
    case 'repor':
      return (
        <ReporItem
          sessao={sessao}
          m={m}
          itemId={tela.itemId}
          aoVoltar={() => ir({ t: 'fila', missaoId: m.missaoId })}
          ir={ir}
        />
      );
    case 'diferenca':
      return <DiferencaRegistrada m={m} tela={tela} ir={ir} />;
    case 'editar':
      return (
        <EditarGiroDeHoje
          sessao={sessao}
          m={m}
          aoVoltar={() => ir({ t: tela.volta, missaoId: m.missaoId })}
        />
      );
  }
}

/** Mensagem única para escrita recusada ou incerta. Incerto nunca vira "recusado". */
function textoDaFalha(r: Escrita<unknown>): string | null {
  if (r.tipo === 'confirmada') return null;
  if (r.tipo === 'recusada') return r.mensagem;
  return 'Não enviado — sem resposta do servidor. O valor continua na tela: toque para repetir.';
}

/* ---------- ficha do produto ---------- */

/**
 * O produto COMPLETO para o balconista achar a caixa certa (dono, 28/09): foto, nome,
 * fabricante, código de barras e código interno. Foto que não carrega vira "SEM FOTO" — nunca
 * ícone quebrado, que faz pensar que a tela está com defeito.
 */
type ProdutoNaTela = Pick<ItemDaLista, 'nome' | 'codigo' | 'ean' | 'fabricante' | 'fotoProduto'>;

export function FichaDoProduto({
  item,
  escura = false,
}: {
  item: ProdutoNaTela;
  escura?: boolean;
}) {
  const [semFoto, setSemFoto] = useState(!item.fotoProduto);
  useEffect(() => setSemFoto(!item.fotoProduto), [item.fotoProduto]);
  return (
    <div className={escura ? 'gb-ficha gb-ficha-escura' : 'gb-ficha'}>
      <div className="gb-ficha-foto">
        {semFoto || !item.fotoProduto ? (
          <span>SEM FOTO</span>
        ) : (
          <img
            src={item.fotoProduto}
            alt={`Foto de ${item.nome}`}
            loading="eager"
            onError={() => setSemFoto(true)}
          />
        )}
      </div>
      <div className="gb-ficha-dados">
        <b className="gb-ficha-nome">{item.nome}</b>
        <em>{item.fabricante ?? 'Fabricante não informado'}</em>
        <dl>
          <div>
            <dt>Cód. barras</dt>
            <dd className="gb-mono">{item.ean ?? '—'}</dd>
          </div>
          <div>
            <dt>Cód. interno</dt>
            <dd className="gb-mono">{item.codigo ?? '—'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

/* ---------- scanner ---------- */

/**
 * O leitor. SÓ abre quando a pessoa toca em "Escanear" (Carlos e dono, 29/09) — nunca ao avançar,
 * salvar, carregar ou retomar. Lê qualquer produto pendente do bloco (ver `itemDoCodigoLido`).
 */
function Scanner({
  sessao,
  itemId,
  itens,
  aoConfirmar,
  aoManual,
  aoNaoAchei,
  esperado,
}: {
  sessao: Sessao;
  itemId: string;
  itens: readonly ItemDaLista[];
  aoConfirmar: (itemId: string, entrada: 'SCAN' | 'MANUAL') => void;
  aoManual: () => void;
  /** "Não achei na prateleira": não há o que escanear, registra direto (dono, 26/09). */
  aoNaoAchei: (r: ResultadoDaContagem) => void;
  esperado: ItemDaLista | undefined;
}) {
  const naoAchei = (
    <NaoAcheiNaPrateleira sessao={sessao} itemId={itemId} aoRegistrar={aoNaoAchei} />
  );
  const video = useRef<HTMLVideoElement | null>(null);
  const fluxo = useRef<MediaStream | null>(null);
  const [camera, setCamera] = useState<'ligando' | 'ok' | 'bloqueada' | 'sem_detector'>('ligando');
  /** "Permitir câmera" pede de novo sem recarregar a página (recarregar jogava para o início). */
  const [tentativa, setTentativa] = useState(0);
  const [lanterna, setLanterna] = useState<'sem' | 'desligada' | 'ligada'>('sem');
  const tela = useRef<HTMLCanvasElement | null>(null);
  const itensVivos = useRef(itens);
  itensVivos.current = itens;
  const [leitura, setLeitura] = useState<ResultadoDaLeitura | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const ocupado = useRef(false);
  const ultima = useRef<UltimaLeitura | null>(null);
  const leituraAtiva = useRef(false);
  leituraAtiva.current = leitura !== null;

  // A leitura mais nova fica num ref: o laço da câmera é criado UMA vez por tela e sempre chama
  // esta. Antes o efeito dependia dela e religava a câmera a cada render do pai.
  const ler = useRef<(codigo: string) => void>(() => undefined);
  ler.current = (codigo: string) => {
    if (ocupado.current || leituraAtiva.current) return;
    if (!podeEnviarLeitura(codigo, ultima.current, Date.now())) return;
    ocupado.current = true;
    setFalha(null);
    const alvo = itemDoCodigoLido(itensVivos.current, codigo, itemId);
    void lerCodigo(sessao, alvo, codigo, 'SCAN').then((r) => {
      ocupado.current = false;
      if (r.tipo === 'confirmada' && r.dados.resultado === 'confirmado') {
        aoConfirmar(alvo, 'SCAN');
        return;
      }
      ultima.current = { codigo, em: Date.now(), aguardaToque: r.tipo !== 'confirmada' };
      if (r.tipo === 'confirmada') setLeitura(r.dados);
      else setFalha(textoDaFalha(r));
    });
  };

  // O <video> sai da tela enquanto o aviso ("produto diferente"…) aparece e volta NOVO depois:
  // o ref de callback religa a câmera nele — sem isso o leitor voltava preto e parado.
  const ligarVideo = useCallback((el: HTMLVideoElement | null) => {
    video.current = el;
    const s = fluxo.current;
    if (el && s && el.srcObject !== s) {
      el.srcObject = s;
      void el.play().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    let parar = false;
    let espera: ReturnType<typeof setTimeout> | undefined;
    // Sem HTTPS o navegador nem expõe a câmera: é "indisponível", não "ligando" para sempre.
    if (!temCamera()) {
      setCamera('sem_detector');
      return;
    }
    setCamera('ligando');
    // iPhone não tem leitor nativo: criarDetector cai no ZXing (antes: "Leitor indisponível").
    let detector: Detector | null = null;
    let semLeitor = false;
    void criarDetector(['ean_13', 'ean_8', 'code_128'])
      .then((d) => {
        detector = d;
      })
      .catch(() => {
        if (parar) return;
        semLeitor = true;
        // Sem leitor a câmera ligada só gasta bateria: desliga.
        fluxo.current?.getTracks().forEach((t) => t.stop());
        fluxo.current = null;
        setCamera('sem_detector');
      });
    void abrirCameraTraseira()
      .then((s) => {
        // A tela saiu antes da câmera ligar: desliga já, senão a câmera fica presa ("em uso").
        // (ou o leitor já falhou: sem ele a câmera não serve para nada).
        if (parar || semLeitor) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        fluxo.current = s;
        ligarVideo(video.current);
        setCamera('ok');
        // Foco contínuo + zoom ~2× (lê a um palmo, onde o foco funciona) — dono, 29/09.
        void ajustarCamera(s).then((a) => {
          if (!parar && a.lanterna) setLanterna('desligada');
        });
        let n = 0;
        const laco = async () => {
          if (parar || semLeitor) return;
          const v = video.current;
          // Enquanto um aviso está na tela (ou uma leitura está indo), o laço espera — não morre.
          // O ZXing pode chegar depois da câmera: até lá o laço só espera.
          if (detector && v && v.readyState >= 2 && !leituraAtiva.current && !ocupado.current) {
            try {
              tela.current ??= document.createElement('canvas');
              const codigo = await lerQuadro(detector, v, tela.current, n++);
              if (codigo && !parar) ler.current(codigo);
            } catch {
              // quadro sem código
            }
          }
          if (!parar) espera = setTimeout(() => void laco(), 100);
        };
        void laco();
      })
      .catch(() => {
        if (!parar) setCamera('bloqueada');
      });
    return () => {
      parar = true;
      clearTimeout(espera);
      fluxo.current?.getTracks().forEach((t) => t.stop());
      fluxo.current = null;
    };
  }, [ligarVideo, tentativa]);

  if (leitura?.resultado === 'produto_diferente')
    return (
      <div className="gb-corpo">
        <div className="gb-cartao gb-cartao-alerta">
          <div className="gb-alerta-topo gb-vermelho-bg">
            <span className="gb-alerta-icone">
              <Icone nome="warn" tamanho={20} traco={1.8} />
            </span>
            <b>Produto diferente</b>
            <p>Este código não corresponde ao produto esperado neste item.</p>
          </div>
          <div className="gb-pad">
            <div className="gb-caixa">
              <em>ESPERADO</em>
              <b>{leitura.esperado.nome}</b>
              <span className="gb-mono">{leitura.esperado.ean ?? esperado?.codigo ?? ''}</span>
            </div>
            <div className="gb-caixa gb-caixa-vermelha">
              <em>LIDO AGORA</em>
              <b>{leitura.lido.nome}</b>
              <span className="gb-mono">{leitura.lido.ean}</span>
            </div>
            <p className="gb-nota">Confira a caixa antes de escanear de novo.</p>
          </div>
        </div>
        <button type="button" className="gb-botao" onClick={() => setLeitura(null)}>
          <Icone nome="cam" tamanho={17} traco={1.8} /> Escanear novamente
        </button>
      </div>
    );
  if (leitura?.resultado === 'nao_reconhecido')
    return (
      <div className="gb-corpo">
        <div className="gb-cartao gb-alerta-topo gb-ambar-bg">
          <span className="gb-alerta-icone gb-alerta-ambar">
            <Icone nome="hash" tamanho={20} traco={1.8} />
          </span>
          <b>Código não reconhecido</b>
          <span className="gb-mono">{leitura.codigo}</span>
          <p>
            Registramos este código para o administrador revisar. Você não altera cadastro por aqui.
          </p>
        </div>
        <button type="button" className="gb-botao" onClick={() => setLeitura(null)}>
          <Icone nome="cam" tamanho={17} traco={1.8} /> Escanear novamente
        </button>
        <button type="button" className="gb-botao gb-botao-linha" onClick={aoManual}>
          <Icone nome="hash" tamanho={17} traco={1.8} /> Digitar código interno
        </button>
      </div>
    );
  if (camera === 'bloqueada' || camera === 'sem_detector')
    return (
      <div className="gb-corpo">
        {esperado && <FichaDoProduto item={esperado} />}
        <div className="gb-cartao gb-camera-off">
          <span className="gb-vazio-icone">
            <Icone nome="offline" tamanho={18} traco={1.7} />
          </span>
          <b>{camera === 'bloqueada' ? 'Câmera sem permissão' : 'Leitor não carregou'}</b>
          <p>
            {camera === 'bloqueada'
              ? 'Permita o uso da câmera para escanear o código.'
              : 'O leitor não carregou (sem internet ou página sem HTTPS). Tente de novo ou digite o código.'}
          </p>
          <button type="button" className="gb-botao" onClick={() => setTentativa((n) => n + 1)}>
            <Icone nome={camera === 'bloqueada' ? 'cam' : 'retry'} tamanho={16} traco={1.8} />{' '}
            {camera === 'bloqueada' ? 'Permitir câmera' : 'Tentar novamente'}
          </button>
          <button type="button" className="gb-botao gb-botao-linha" onClick={aoManual}>
            <Icone nome="hash" tamanho={16} traco={1.8} /> Digitar código
          </button>
          {naoAchei}
        </div>
      </div>
    );
  return (
    <div className="gb-scanner">
      <video
        ref={ligarVideo}
        playsInline
        muted
        aria-label="Câmera — toque para focar"
        onClick={() => fluxo.current && void focarDeNovo(fluxo.current)}
      />
      {lanterna !== 'sem' && (
        <button
          type="button"
          className="gb-lanterna"
          aria-pressed={lanterna === 'ligada'}
          onClick={() => {
            const s = fluxo.current;
            if (!s) return;
            const ligar = lanterna !== 'ligada';
            void usarLanterna(s, ligar).then(
              (ok) => ok && setLanterna(ligar ? 'ligada' : 'desligada'),
            );
          }}
        >
          {lanterna === 'ligada' ? 'Desligar lanterna' : 'Lanterna'}
        </button>
      )}
      {esperado && (
        <div className="gb-scanner-ficha">
          <FichaDoProduto item={esperado} escura />
        </div>
      )}
      <div className="gb-mira">
        <i />
      </div>
      <div className="gb-scanner-texto">
        <b>Aponte para o código de barras</b>
        <em>A um palmo da caixa · toque na imagem para focar</em>
        {falha && (
          <p className="gb-falha" role="alert">
            {falha}{' '}
            <button
              type="button"
              className="gb-link gb-link-peq"
              onClick={() => {
                // O mesmo código só vai de novo quando a pessoa pede.
                ultima.current = null;
                setFalha(null);
              }}
            >
              Tentar de novo
            </button>
          </p>
        )}
      </div>
      <button type="button" className="gb-scanner-manual" onClick={aoManual}>
        <Icone nome="hash" tamanho={16} traco={1.7} />
        <div>
          <b>Não consegue escanear?</b>
          <em>Digitar o código ou tocar no produto na lista</em>
        </div>
        <Icone nome="chev-r" tamanho={14} traco={1.7} />
      </button>
      {naoAchei}
    </div>
  );
}

function NaoAcheiNaPrateleira({
  sessao,
  itemId,
  aoRegistrar,
}: {
  sessao: Sessao;
  itemId: string;
  aoRegistrar: (r: ResultadoDaContagem) => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  if (!confirmando)
    return (
      <button type="button" className="gb-link gb-nao-achei" onClick={() => setConfirmando(true)}>
        <Icone nome="search" tamanho={14} traco={1.7} /> Não achei na prateleira
      </button>
    );
  return (
    <div className="gb-cartao gb-nao-achei-confirma">
      <b>Não achou nenhuma unidade na prateleira?</b>
      <p className="gb-nota">
        Procure bem antes: o auditor confere, e se achar o produto a sua venda fica bloqueada no dia
        seguinte.
      </p>
      {falha && (
        <p className="gb-falha" role="alert">
          {falha}
        </p>
      )}
      <div className="gb-nao-achei-botoes">
        <button
          type="button"
          className="gb-botao gb-botao-linha"
          disabled={enviando}
          onClick={() => setConfirmando(false)}
        >
          Voltar e procurar
        </button>
        <button
          type="button"
          className="gb-botao"
          disabled={enviando}
          onClick={() => {
            setEnviando(true);
            setFalha(null);
            void contar(sessao, itemId, 0, true).then((r) => {
              setEnviando(false);
              if (r.tipo === 'confirmada') aoRegistrar(r.dados);
              else setFalha(textoDaFalha(r));
            });
          }}
        >
          Confirmo: não achei
        </button>
      </div>
    </div>
  );
}

/**
 * A LISTA do bloco (dono, 29/09 — igual à conferência de prateleira do 1.0): cada produto com foto
 * pequena, nome, fabricante e código. Tocar no produto abre a quantidade; a câmera pequena do
 * produto (ou "Escanear código") abre o leitor — só por toque; o campo aceita EAN ou código
 * interno. Nenhuma quantidade de estoque aparece aqui.
 */
function ListaDeProdutos({
  sessao,
  itemId,
  itens,
  avisoInicial,
  aoLimparAviso,
  aoAbrir,
  aoIdentificado,
  aoEscanear,
}: {
  sessao: Sessao;
  /** O próximo produto da ordem física: destacado, e o esperado de um código que não bate. */
  itemId: string;
  itens: readonly ItemDaLista[];
  avisoInicial: string | null;
  aoLimparAviso: () => void;
  aoAbrir: (itemId: string) => Promise<string | null>;
  aoIdentificado: (itemId: string, entrada: 'SCAN' | 'MANUAL') => void;
  aoEscanear: (itemId: string) => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [falha, setFalha] = useState<string | null>(avisoInicial);
  const [lido, setLido] = useState<ResultadoDaLeitura | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const ocupado = useRef(false);
  const atual = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (avisoInicial) aoLimparAviso();
    atual.current?.scrollIntoView({ block: 'center' });
    // Só ao abrir a lista.
  }, []);
  const comecar = (id: string) => {
    if (ocupado.current) return false;
    ocupado.current = true;
    setAbrindo(id);
    setFalha(null);
    setLido(null);
    return true;
  };
  const terminar = () => {
    ocupado.current = false;
    setAbrindo(null);
  };
  const tocar = (id: string) => {
    if (!comecar(id)) return;
    void aoAbrir(id).then((f) => {
      terminar();
      if (f) setFalha(f);
    });
  };
  const enviarCodigo = (valor: string) => {
    const alvo = itemDoCodigoLido(itens, valor, itemId);
    if (!comecar(alvo)) return;
    void lerCodigo(sessao, alvo, valor, 'MANUAL').then((r) => {
      terminar();
      if (r.tipo === 'confirmada') {
        if (r.dados.resultado === 'confirmado') aoIdentificado(alvo, 'MANUAL');
        else setLido(r.dados);
      } else setFalha(textoDaFalha(r));
    });
  };
  const filtro = codigo.trim().toLowerCase();
  const lista = itens.filter(
    (i) =>
      i.situacao !== 'transferido' &&
      (!filtro ||
        (i.codigo ?? '').startsWith(filtro) ||
        (i.ean ?? '').startsWith(filtro) ||
        i.nome.toLowerCase().includes(filtro)),
  );
  return (
    <div className="gb-corpo gb-troca">
      <div className="gb-busca">
        <label className="gb-codigo">
          <em>EAN OU CÓDIGO INTERNO</em>
          <input
            inputMode="numeric"
            value={codigo}
            placeholder="Digite o código do produto"
            onChange={(e) => setCodigo(e.target.value.replace(/\s/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && codigo && enviarCodigo(codigo)}
            aria-label="EAN ou código interno"
          />
        </label>
        {codigo ? (
          <button
            type="button"
            className="gb-botao gb-busca-botao"
            disabled={abrindo !== null}
            onClick={() => enviarCodigo(codigo)}
          >
            {abrindo !== null ? 'Conferindo…' : 'OK'}
          </button>
        ) : (
          <button
            type="button"
            className="gb-botao gb-busca-botao"
            onClick={() => aoEscanear(itemId)}
            aria-label="Escanear código"
          >
            <Icone nome="cam" tamanho={18} traco={1.8} /> Escanear
          </button>
        )}
      </div>
      {lido && lido.resultado !== 'confirmado' && (
        <p className="gb-falha" role="alert">
          {lido.resultado === 'produto_diferente'
            ? `Este código é de outro item: ${lido.lido.nome}.`
            : 'Código não reconhecido — registrado para o administrador revisar.'}
        </p>
      )}
      {falha && (
        <p className="gb-falha" role="alert">
          {falha}
        </p>
      )}
      <div className="gb-cartao gb-lista gb-lista-manual">
        <span className="gb-rotulo">Produtos deste bloco · toque para contar</span>
        <ul>
          {lista.map((i) => {
            const pendente = i.situacao === 'pendente';
            return (
              <li
                key={i.id}
                ref={i.id === itemId ? atual : undefined}
                className={`${i.id === itemId ? 'gb-lista-atual' : ''} ${pendente ? '' : 'gb-lista-feito'}`}
              >
                <button
                  type="button"
                  className="gb-linha-produto"
                  onClick={() => tocar(i.id)}
                  disabled={!pendente || abrindo !== null}
                >
                  <FotoPequena item={i} />
                  <div>
                    <b>{i.nome}</b>
                    <em>{linhaDoProduto(i.fabricante, i.codigo)}</em>
                  </div>
                  {abrindo === i.id ? (
                    <span className="gb-nota">Abrindo…</span>
                  ) : !pendente ? (
                    <span className="gb-nota gb-feito">
                      <Icone nome="check" tamanho={13} traco={2} /> contado
                    </span>
                  ) : null}
                </button>
                {pendente && (
                  <button
                    type="button"
                    className="gb-linha-camera"
                    aria-label={`Escanear ${i.nome}`}
                    disabled={abrindo !== null}
                    onClick={() => aoEscanear(i.id)}
                  >
                    <Icone nome="cam" tamanho={18} traco={1.8} />
                  </button>
                )}
              </li>
            );
          })}
          {lista.length === 0 && (
            <li className="gb-nota gb-pad">Nenhum produto com esse código.</li>
          )}
        </ul>
      </div>
      <p className="gb-nota">
        Tocar no produto ou digitar o código registra como <b>entrada manual</b>; a câmera registra
        como escaneado.
      </p>
    </div>
  );
}

/** Foto pequena na lista: sem foto (ou foto quebrada) vira um quadrado neutro, nunca ícone quebrado. */
function FotoPequena({ item }: { item: Pick<ItemDaLista, 'fotoProduto'> }) {
  const [semFoto, setSemFoto] = useState(!item.fotoProduto);
  return (
    <span className="gb-foto-peq" aria-hidden="true">
      {!semFoto && item.fotoProduto ? (
        <img src={item.fotoProduto} alt="" loading="lazy" onError={() => setSemFoto(true)} />
      ) : (
        <Icone nome="box" tamanho={16} traco={1.5} />
      )}
    </span>
  );
}

/* ---------- contagem ---------- */

/**
 * A quantidade (Carlos, 29/09): atalhos 0 a 6 + "Digitar", um só "Confirmar X unidades". Só
 * avança quando o servidor CONFIRMA; falhou, fica aqui com o número na tela. Sem tela "Repor X"
 * depois: o próximo produto abre direto.
 */
function Contagem({
  sessao,
  missaoId,
  item,
  itens,
  entrada,
  aoSalvar,
  aoPular,
}: {
  sessao: Sessao;
  missaoId: string;
  item: ItemDaLista;
  itens: readonly ItemDaLista[];
  entrada: 'SCAN' | 'MANUAL';
  aoSalvar: (r: ResultadoDaContagem) => void;
  /** Pular: devolve o motivo quando não deu (ex.: é o último produto). */
  aoPular: () => Promise<string | null>;
}) {
  const [qtd, setQtd] = useState(0);
  const [digitar, setDigitar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [lento, setLento] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  // Toque duplo: o estado só muda no próximo render — o ref barra o segundo toque na hora.
  const ocupado = useRef(false);
  const [pulando, setPulando] = useState(false);
  const pular = () => {
    if (ocupado.current || pulando) return;
    setPulando(true);
    setFalha(null);
    void aoPular().then((motivo) => {
      setPulando(false);
      if (motivo) setFalha(motivo);
    });
  };
  // "Salvando contagem…" só se passar de 250 ms: salvar rápido não pisca a tela.
  useEffect(() => {
    if (!enviando) return setLento(false);
    const t = setTimeout(() => setLento(true), 250);
    return () => clearTimeout(t);
  }, [enviando]);
  // Pré-carrega a foto do próximo produto enquanto este é contado: a troca fica instantânea.
  const proximaFoto = itens.find(
    (i) => i.situacao === 'pendente' && i.ordem > item.ordem,
  )?.fotoProduto;
  useEffect(() => {
    if (proximaFoto) new Image().src = proximaFoto;
  }, [proximaFoto]);
  const salvar = (naoLocalizado: boolean) => {
    if (ocupado.current) return;
    ocupado.current = true;
    setEnviando(true);
    setFalha(null);
    void contar(sessao, item.id, naoLocalizado ? 0 : qtd, naoLocalizado).then((r) => {
      ocupado.current = false;
      setEnviando(false);
      if (r.tipo === 'confirmada') aoSalvar(r.dados);
      else setFalha(textoDaFalha(r));
    });
  };
  const unidades = `${String(qtd)} ${qtd === 1 ? 'unidade' : 'unidades'}`;
  return (
    <div className="gb-corpo gb-troca">
      <FichaDoProduto item={item} />
      {entrada === 'SCAN' && (
        <p className="gb-nota gb-escaneado">
          <Icone nome="check" tamanho={13} traco={2} /> Produto escaneado
        </p>
      )}
      <div className="gb-cartao gb-contador">
        <b>Quantas unidades estão na prateleira?</b>
        <div className="gb-stepper">
          <button
            type="button"
            aria-label="Menos uma"
            disabled={enviando}
            onClick={() => setQtd((q) => Math.max(0, q - 1))}
          >
            <Icone nome="minus" tamanho={20} traco={1.8} />
          </button>
          {digitar ? (
            <input
              autoFocus
              inputMode="numeric"
              value={String(qtd)}
              onChange={(e) =>
                setQtd(Math.min(9999, Number(e.target.value.replace(/\D/g, '') || 0)))
              }
              onKeyDown={(e) => e.key === 'Enter' && salvar(false)}
              aria-label="Quantidade na prateleira"
            />
          ) : (
            <output aria-live="polite">{qtd}</output>
          )}
          <button
            type="button"
            aria-label="Mais uma"
            disabled={enviando}
            onClick={() => setQtd((q) => Math.min(9999, q + 1))}
          >
            <Icone nome="plus" tamanho={20} traco={1.8} />
          </button>
        </div>
        <div className="gb-rapidos" role="group" aria-label="Atalhos de quantidade">
          {ATALHOS_DE_QUANTIDADE.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={qtd === n && !digitar}
              className={qtd === n && !digitar ? 'gb-rapido-sel' : ''}
              disabled={enviando}
              onClick={() => {
                setDigitar(false);
                setQtd(n);
              }}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={digitar}
            className={digitar ? 'gb-rapido-sel' : ''}
            disabled={enviando}
            onClick={() => setDigitar(true)}
          >
            Digitar
          </button>
        </div>
      </div>
      <RegistrarOcorrencia sessao={sessao} missaoId={missaoId} itemId={item.id} />
      {falha && (
        <div className="gb-falha" role="alert">
          <b>Não foi possível salvar a contagem.</b> {falha}
        </div>
      )}
      <div className="gb-fixo">
        <button
          type="button"
          className="gb-botao gb-botao-grande"
          disabled={enviando}
          aria-busy={enviando}
          onClick={() => salvar(false)}
        >
          {enviando
            ? lento
              ? 'Salvando contagem…'
              : `Confirmar ${unidades}`
            : falha
              ? `Tentar novamente · ${unidades}`
              : `Confirmar ${unidades}`}
        </button>
        <button type="button" className="gb-link" disabled={enviando} onClick={() => salvar(true)}>
          <Icone nome="search" tamanho={14} traco={1.7} /> Não localizei o produto
        </button>
        <button
          type="button"
          className="gb-link gb-pular"
          disabled={enviando || pulando}
          onClick={pular}
          aria-label="Pular este produto: fica para o fim da fila"
        >
          <Icone nome="clock" tamanho={14} traco={1.7} />{' '}
          {pulando ? 'Pulando…' : 'Pular produto · fica para o fim'}
        </button>
      </div>
    </div>
  );
}

/** A foto de auditoria sorteada: carimba, envia e confirma no item. Usada no resultado e na volta. */
function CartaoDaFoto({
  sessao,
  item,
  nome,
  fotoOk,
  aoEnviar,
  texto,
}: {
  sessao: Sessao;
  item: ItemDaLista;
  nome: string;
  fotoOk: boolean;
  aoEnviar: () => void;
  texto: string;
}) {
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);
  // A prévia é um arquivo na memória: solta quando troca ou quando a tela sai.
  useEffect(
    () => () => {
      if (previa) URL.revokeObjectURL(previa);
    },
    [previa],
  );
  const tirar = (arq: FonteDaFoto | undefined) => {
    if (!arq || enviando) return;
    setEnviando(true);
    setFalha(null);
    void (async () => {
      try {
        const blob = await carimbarFoto(arq, [
          `Giro · ${item.nome}`,
          `${nome} · ${agoraCarimbo()}`,
        ]);
        setPrevia(URL.createObjectURL(blob));
        const up = await enviarFoto(sessao, blob);
        if (!up.ok) {
          setFalha(up.mensagem);
          return;
        }
        const f = await fotoDoItem(sessao, item.id, up.id);
        if (f.tipo === 'confirmada') aoEnviar();
        else setFalha(textoDaFalha(f));
      } catch {
        setFalha('Não foi possível preparar a foto. Tente de novo.');
      } finally {
        setEnviando(false);
      }
    })();
  };
  return (
    <div className="gb-cartao gb-auditoria">
      <div className="gb-auditoria-topo">
        <b>
          <Icone nome="cam" tamanho={15} traco={1.7} /> Auditoria rápida
        </b>
        <p>{texto}</p>
      </div>
      <div className="gb-pad">
        <h2 className="gb-produto gb-produto-peq">{item.nome}</h2>
        <span className="gb-nota gb-mono">{item.codigo ?? ''}</span>
        <div className="gb-foto">
          {previa ? (
            <img src={previa} alt="Foto da prateleira" />
          ) : (
            <>
              <Icone nome="cam" tamanho={26} traco={1.5} />
              <p>
                Fotografe a prateleira do produto.<em>Evite pessoas na imagem.</em>
              </p>
            </>
          )}
        </div>
        {fotoOk ? (
          <p className="gb-ok">
            <Icone nome="check" tamanho={14} traco={2} /> Foto enviada
          </p>
        ) : (
          <button
            type="button"
            className="gb-botao gb-botao-escuro"
            onClick={() => setCamera(true)}
            disabled={enviando}
          >
            <Icone nome="cam" tamanho={17} traco={1.8} /> {enviando ? 'Enviando…' : 'Abrir câmera'}
          </button>
        )}
        {camera && (
          <CameraDaFoto
            titulo="Foto da prateleira"
            aoFechar={() => setCamera(false)}
            aoUsar={(f) => {
              setCamera(false);
              tirar(f);
            }}
          />
        )}
        {falha && (
          <p className="gb-falha" role="alert">
            {falha}
          </p>
        )}
        <p className="gb-nota gb-centro">
          Obrigatório para este item — a foto precisa ser confirmada
        </p>
      </div>
    </div>
  );
}

/**
 * A foto que ficou para trás: a contagem foi salva, mas a foto sorteada não chegou (a pessoa saiu
 * da tela, a rede caiu). Sem esta tela o resumo recusava e não havia como tirá-la — o dia travava.
 */
function FotoQueFaltou({
  sessao,
  item,
  nome,
  aoProximo,
}: {
  sessao: Sessao;
  item: ItemDaLista;
  nome: string;
  aoProximo: () => void;
}) {
  const [fotoOk, setFotoOk] = useState(item.fotoEnviada);
  return (
    <div className="gb-corpo">
      <div className="gb-faixa gb-faixa-ambar gb-faixa-fina">
        <Icone nome="warn" tamanho={15} traco={1.8} />
        <b>A contagem deste produto já está salva — falta só a foto</b>
      </div>
      <CartaoDaFoto
        sessao={sessao}
        item={item}
        nome={nome}
        fotoOk={fotoOk}
        aoEnviar={() => setFotoOk(true)}
        texto="Este produto foi sorteado para foto da prateleira. Sem ela o seu Giro não termina."
      />
      <div className="gb-fixo">
        <button
          type="button"
          className={`gb-botao ${fotoOk ? '' : 'gb-botao-off'}`}
          disabled={!fotoOk}
          onClick={aoProximo}
        >
          Continuar
        </button>
        {!fotoOk && <p className="gb-nota gb-centro">Disponível após o envio da foto</p>}
      </div>
    </div>
  );
}

/* ---------- reposição ---------- */

function ResumoDaContagemTela({
  sessao,
  m,
  aoVoltar,
  ir,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  aoVoltar: () => void;
  ir: (t: Tela) => void;
}) {
  const [r, setR] = useState<ResumoDaContagem | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [trabalhando, setTrabalhando] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    setFalha(null);
    if (m.estado === 'COUNTING' || m.estado === 'AVAILABLE')
      void concluirContagem(sessao, m.missaoId).then((x) => {
        if (x.tipo === 'confirmada') setR(x.dados);
        else setFalha(textoDaFalha(x));
      });
    else
      void lerResumo(m.missaoId).then((x) => {
        if (x.ok) setR(x.dados);
        else setFalha('Não foi possível carregar o resumo. Confira a conexão e tente de novo.');
      });
  }, [sessao, m.missaoId, m.estado, tentativa]);
  const seguir = () => {
    if (!r) return;
    setTrabalhando(true);
    setFalha(null);
    if (r.precisamRepor === 0) {
      void finalizar(sessao, m.missaoId).then((f) => {
        setTrabalhando(false);
        if (f.tipo === 'confirmada')
          ir({
            t: 'fim',
            missaoId: m.missaoId,
            resumo: r,
            repostos: 0,
            unidades: 0,
            diferencas: 0,
            liberado: f.dados.liberado,
          });
        else setFalha(textoDaFalha(f));
      });
      return;
    }
    void iniciarReposicao(sessao, m.missaoId).then((x) => {
      setTrabalhando(false);
      if (x.tipo === 'confirmada') ir({ t: 'fila', missaoId: m.missaoId });
      else setFalha(textoDaFalha(x));
    });
  };
  return (
    <>
      <header className="gb-cab">
        <span className="gb-marca" aria-hidden="true">
          +
        </span>
        <div>
          <b>Giro de Prateleira</b>
          <em className="gb-mono">
            {m.setor} · {bN(m.numero)} · {m.faixa}
          </em>
        </div>
        <span style={{ flex: 1 }} />
        <button type="button" className="gb-menu" aria-label="Voltar" onClick={aoVoltar}>
          <Icone nome="x" tamanho={16} traco={1.8} />
        </button>
      </header>
      <div className="gb-corpo">
        {falha && !r ? (
          <div className="gb-cartao gb-erro">
            <b>A contagem ainda não pode ser encerrada</b>
            <p>{falha}</p>
            <button type="button" className="gb-botao" onClick={() => setTentativa((n) => n + 1)}>
              <Icone nome="retry" tamanho={15} traco={1.8} /> Tentar de novo
            </button>
            <button type="button" className="gb-botao gb-botao-linha" onClick={aoVoltar}>
              Voltar ao bloco
            </button>
          </div>
        ) : !r ? (
          <div className="gb-esqueleto" aria-busy="true">
            <i />
          </div>
        ) : (
          <>
            <div className="gb-cartao">
              <div className="gb-concluida">
                <span className="gb-check-grande">
                  <Icone nome="check" tamanho={20} traco={2.4} />
                </span>
                <div>
                  <b>Contagem concluída</b>
                  <p>
                    {r.conferidos} de {r.total} produtos
                    {r.iniciadaEm && r.concluidaEm
                      ? ` · ${hora(r.iniciadaEm)} às ${hora(r.concluidaEm)}`
                      : ''}
                  </p>
                </div>
              </div>
              <ul className="gb-resumo">
                <li>
                  <Icone nome="box" tamanho={15} traco={1.7} /> Precisam de reposição{' '}
                  <b>{r.precisamRepor}</b>
                </li>
                <li>
                  <Icone nome="clock" tamanho={15} traco={1.7} /> Aguardam estoque do sistema{' '}
                  <b>{r.aguardandoEstoque}</b>
                </li>
                <li>
                  <Icone nome="cam" tamanho={15} traco={1.7} /> Fotos enviadas <b>{r.fotos}</b>
                </li>
                <li>
                  <Icone nome="search" tamanho={15} traco={1.7} /> Não localizados{' '}
                  <b>{r.naoLocalizados}</b>
                </li>
              </ul>
            </div>
            {r.aguardandoEstoque > 0 && (
              <div className="gb-cartao gb-aviso">
                <Icone nome="clock" tamanho={15} traco={1.7} />
                <p>
                  Os {r.aguardandoEstoque} itens que aguardam estoque não são pendência sua — eles
                  entram na fila quando os dados voltarem.
                </p>
              </div>
            )}
            {r.precisamRepor > 0 && (
              <div className="gb-cartao gb-cartao-vermelho">
                <b>Seu Giro ainda não terminou</b>
                <p>
                  Falta repor {r.precisamRepor} produtos · {r.unidades} unidades solicitadas. As
                  atividades do InovaPay abrem depois disso.
                </p>
              </div>
            )}
            {falha && (
              <p className="gb-falha" role="alert">
                {falha}
              </p>
            )}
            <div className="gb-fixo">
              <button type="button" className="gb-botao" disabled={trabalhando} onClick={seguir}>
                <Icone nome="box" tamanho={17} traco={1.8} />{' '}
                {r.precisamRepor ? 'Iniciar reposição' : 'Concluir o Giro'}
              </button>
              <button
                type="button"
                className="gb-botao gb-botao-linha"
                disabled={trabalhando}
                onClick={() => ir({ t: 'editar', missaoId: m.missaoId, volta: 'resumo' })}
              >
                <Icone nome="edit" tamanho={15} traco={1.8} /> Editar giro de hoje
              </button>
              <ol className="gb-trilha gb-trilha-solta">
                <li className="gb-trilha-feito">
                  <Icone nome="check" tamanho={11} traco={2.4} />
                  Conferência
                </li>
                <li className={r.precisamRepor ? 'gb-trilha-atual' : 'gb-trilha-feito'}>
                  <i />
                  Reposição
                </li>
                <li>
                  <i />
                  InovaPay
                </li>
              </ol>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function FilaDeReposicao({
  sessao,
  m,
  aoVoltar,
  ir,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  aoVoltar: () => void;
  ir: (t: Tela) => void;
}) {
  const [f, setF] = useState<{
    itens: ItemDaReposicao[];
    aguardando: string[];
    unidades: number;
  } | null>(null);
  const [falhou, setFalhou] = useState(false);
  const carregar = useCallback(() => {
    setFalhou(false);
    void lerReposicao(m.missaoId).then((r) => {
      if (r.ok) setF(r.dados);
      else setFalhou(true);
    });
  }, [m.missaoId]);
  useEffect(carregar, [carregar]);
  const feitos = f?.itens.filter((i) => i.feito).length ?? 0;
  const proximo = f?.itens.find((i) => !i.feito);
  return (
    <>
      <header className="gb-fluxo-cab">
        <button type="button" className="gb-voltar" aria-label="Voltar" onClick={aoVoltar}>
          <Icone nome="arrow-l" tamanho={18} traco={1.8} />
        </button>
        <div>
          <b>Reposição</b>
          <em className="gb-mono">
            {m.setor} · {bN(m.numero)} · {m.faixa}
          </em>
        </div>
        <span style={{ flex: 1 }} />
        <b className="gb-pct">
          {feitos} / {f?.itens.length ?? 0}
        </b>
      </header>
      <div className="gb-corpo">
        {!f && falhou ? (
          <FalhaAoCarregar oQue="a reposição" aoTentar={carregar} aoVoltar={aoVoltar} />
        ) : !f ? (
          <div className="gb-esqueleto" aria-busy="true">
            <i />
          </div>
        ) : (
          <>
            <div className="gb-tres gb-tres-caixas">
              <div>
                <em>PRODUTOS</em>
                <b>{f.itens.length}</b>
              </div>
              <div>
                <em>UNIDADES</em>
                <b>{f.unidades}</b>
              </div>
              <div>
                <em>FEITOS</em>
                <b>{feitos}</b>
              </div>
            </div>
            <p className="gb-nota">
              {f.unidades} é a soma das unidades solicitadas, não a contagem de produtos.
            </p>
            {f.itens.map((i, k) => (
              <div
                key={i.id}
                className={`gb-cartao gb-fila-item ${i.feito ? 'gb-fila-feito' : ''}`}
              >
                <div className="gb-fila-topo">
                  <FotoPequena item={i} />
                  <div>
                    <b>{i.nome}</b>
                    <em>{linhaDoProduto(i.fabricante, i.codigo)}</em>
                    <em className="gb-mono">EAN {i.ean ?? 'não informado'}</em>
                    {k === 0 && !i.feito ? null : (
                      <em>
                        prateleira {i.prateleira} · alvo {i.alvo} · estoque{' '}
                        {i.estoque ?? 'indisponível'}
                      </em>
                    )}
                  </div>
                  <div className="gb-dir">
                    <em>{i.feito ? 'REPOSTO' : 'BUSCAR'}</em>
                    <strong className={k === 0 && !i.feito ? 'gb-vermelho' : ''}>
                      {i.feito ? i.reposto : i.repor}
                    </strong>
                  </div>
                </div>
                {k === 0 && !i.feito && (
                  <div className="gb-fila-abrir">
                    <div className="gb-tres">
                      <div>
                        <em>Prateleira</em>
                        <b>{i.prateleira}</b>
                      </div>
                      <div>
                        <em>Alvo</em>
                        <b>{i.alvo}</b>
                      </div>
                      <div>
                        <em>Estoque</em>
                        <b>{i.estoque ?? 'indisponível'}</b>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="gb-botao gb-botao-peq"
                      onClick={() => ir({ t: 'repor', missaoId: m.missaoId, itemId: i.id })}
                    >
                      Abrir
                    </button>
                  </div>
                )}
              </div>
            ))}
            {f.aguardando.length > 0 && (
              <div className="gb-cartao gb-aviso">
                <Icone nome="clock" tamanho={15} traco={1.7} />
                <p>
                  <b>{f.aguardando.length} itens aguardando estoque</b>
                  <br />
                  {f.aguardando.join(' e ')}. Sem saldo confiável ainda — não vamos te mandar buscar
                  quantidade indefinida.
                </p>
              </div>
            )}
            <div className="gb-fixo">
              {proximo ? (
                <button
                  type="button"
                  className="gb-botao"
                  onClick={() => ir({ t: 'repor', missaoId: m.missaoId, itemId: proximo.id })}
                >
                  {feitos ? 'Continuar' : 'Começar pelo primeiro'}
                </button>
              ) : (
                <ConcluirGiro
                  sessao={sessao}
                  m={m}
                  ir={ir}
                  separou={f.itens.some((i) => (i.reposto ?? 0) > 0)}
                />
              )}
              <button
                type="button"
                className="gb-botao gb-botao-linha"
                onClick={() => ir({ t: 'editar', missaoId: m.missaoId, volta: 'fila' })}
              >
                <Icone nome="edit" tamanho={15} traco={1.8} /> Editar giro de hoje
              </button>
              <a className="gb-problema" href="#/conta">
                <Icone nome="pin" tamanho={13} traco={1.6} /> Informar problema
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ConcluirGiro({
  sessao,
  m,
  ir,
  separou,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  ir: (t: Tela) => void;
  /** Alguma unidade foi separada para repor: aí a foto da caixa é exigida (dono, 29/09). */
  separou: boolean;
}) {
  const [falha, setFalha] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [fotoOk, setFotoOk] = useState(m.fotoDaCaixa === 'enviada');
  const fechar = () => {
    if (enviando) return;
    setEnviando(true);
    setFalha(null);
    void Promise.all([
      finalizar(sessao, m.missaoId),
      lerResumo(m.missaoId),
      lerReposicao(m.missaoId),
    ]).then(([f, r, fila]) => {
      setEnviando(false);
      if (f.tipo !== 'confirmada') return setFalha(textoDaFalha(f));
      const itens = fila.ok ? fila.dados.itens : [];
      ir({
        t: 'fim',
        missaoId: m.missaoId,
        resumo: r.ok
          ? r.dados
          : {
              conferidos: 0,
              total: 0,
              precisamRepor: 0,
              unidades: 0,
              aguardandoEstoque: 0,
              fotos: 0,
              naoLocalizados: 0,
              iniciadaEm: null,
              concluidaEm: null,
            },
        ...resumoDaReposicao(itens),
        liberado: f.dados.liberado,
      });
    });
  };
  if (separou && !fotoOk)
    return <FotoDaCaixa sessao={sessao} m={m} aoEnviar={() => setFotoOk(true)} />;
  return (
    <>
      {separou && (
        <p className="gb-ok">
          <Icone nome="check" tamanho={14} traco={2} /> Foto da caixa enviada
        </p>
      )}
      <button type="button" className="gb-botao" disabled={enviando} onClick={fechar}>
        <Icone nome="check" tamanho={17} traco={2} /> {enviando ? 'Concluindo…' : 'Concluir o Giro'}
      </button>
      {falha && (
        <p className="gb-falha" role="alert">
          {falha}
        </p>
      )}
    </>
  );
}

/**
 * A FOTO DA CAIXA (dono, 29/09): separou tudo no estoque → "tire uma foto da caixa com os
 * produtos que você separou para repor na prateleira". Carimbada (bloco e hora) e enviada; só
 * então o Giro conclui — o servidor também recusa finalizar sem ela.
 */
function FotoDaCaixa({
  sessao,
  m,
  aoEnviar,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  aoEnviar: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [camera, setCamera] = useState(false);
  // A prévia é um arquivo na memória: solta quando troca ou quando a tela sai.
  useEffect(
    () => () => {
      if (previa) URL.revokeObjectURL(previa);
    },
    [previa],
  );
  const tirar = (arq: FonteDaFoto | undefined) => {
    if (!arq || enviando) return;
    setEnviando(true);
    setFalha(null);
    void (async () => {
      try {
        const blob = await carimbarFoto(arq, [
          `Giro · caixa da reposição · ${m.setor} ${bN(m.numero)}`,
          agoraCarimbo(),
        ]);
        setPrevia(URL.createObjectURL(blob));
        const up = await enviarFoto(sessao, blob);
        if (!up.ok) {
          setFalha(up.mensagem);
          return;
        }
        const f = await fotoDaReposicao(sessao, m.missaoId, up.id);
        if (f.tipo === 'confirmada') aoEnviar();
        else setFalha(textoDaFalha(f));
      } catch {
        setFalha('Não foi possível preparar a foto. Tente de novo.');
      } finally {
        setEnviando(false);
      }
    })();
  };
  return (
    <div className="gb-cartao gb-auditoria">
      <div className="gb-auditoria-topo">
        <b>
          <Icone nome="check" tamanho={15} traco={2} /> Separação concluída!
        </b>
        <p>
          Agora, para confirmar, tire uma foto da caixa com os produtos que você separou para repor
          na prateleira.
        </p>
      </div>
      <div className="gb-pad">
        <div className="gb-foto">
          {previa ? (
            <img src={previa} alt="Foto da caixa da reposição" />
          ) : (
            <>
              <Icone nome="box" tamanho={26} traco={1.5} />
              <p>
                Todos os produtos separados dentro da caixa.<em>Evite pessoas na imagem.</em>
              </p>
            </>
          )}
        </div>
        <button
          type="button"
          className="gb-botao gb-botao-escuro"
          onClick={() => setCamera(true)}
          disabled={enviando}
        >
          <Icone nome="cam" tamanho={17} traco={1.8} />{' '}
          {enviando ? 'Enviando…' : 'Tirar foto da caixa'}
        </button>
        {camera && (
          <CameraDaFoto
            titulo="Foto da caixa da reposição"
            aoFechar={() => setCamera(false)}
            aoUsar={(f) => {
              setCamera(false);
              tirar(f);
            }}
          />
        )}
        {falha && (
          <p className="gb-falha" role="alert">
            {falha}
          </p>
        )}
        <p className="gb-nota gb-centro">Obrigatória para concluir o Giro</p>
      </div>
    </div>
  );
}

function ReporItem({
  sessao,
  m,
  itemId,
  aoVoltar,
  ir,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  itemId: string;
  aoVoltar: () => void;
  ir: (t: Tela) => void;
}) {
  const [f, setF] = useState<{ itens: ItemDaReposicao[] } | null>(null);
  const [modo, setModo] = useState<'escolha' | 'parte' | 'cabe'>('escolha');
  const [achou, setAchou] = useState(1);
  const [cabem, setCabem] = useState<number | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const [pulando, setPulando] = useState(false);
  const carregar = useCallback(() => {
    setFalhou(false);
    void lerReposicao(m.missaoId).then((r) => {
      if (r.ok) setF(r.dados);
      else setFalhou(true);
    });
  }, [m.missaoId]);
  useEffect(carregar, [carregar]);
  const i = f?.itens.find((x) => x.id === itemId);
  /**
   * PULAR NO ESTOQUE (dono, 13/10): não achou a caixa agora → o produto vai para o fim da fila e
   * o próximo já abre. O último que falta o servidor não deixa pular (a tela mostra o motivo).
   */
  const pular = () => {
    if (!i || pulando || enviando) return;
    setPulando(true);
    setFalha(null);
    void pularNaReposicao(sessao, i.id).then(async (r) => {
      if (r.tipo !== 'confirmada') {
        setPulando(false);
        return setFalha(textoDaFalha(r));
      }
      const l = await lerReposicao(m.missaoId);
      setPulando(false);
      const prox = l.ok ? l.dados.itens.find((x) => !x.feito) : undefined;
      if (prox && prox.id !== i.id) {
        setModo('escolha');
        ir({ t: 'repor', missaoId: m.missaoId, itemId: prox.id });
      } else ir({ t: 'fila', missaoId: m.missaoId });
    });
  };
  const pos = f ? f.itens.findIndex((x) => x.id === itemId) + 1 : 0;
  const pct = f
    ? Math.round((f.itens.filter((x) => x.feito).length / Math.max(1, f.itens.length)) * 100)
    : 0;
  const registrar = (
    encontrado: number,
    reposto = encontrado,
    capacidade: number | null = null,
  ) => {
    if (!i) return;
    setEnviando(true);
    setFalha(null);
    void repor(sessao, i.id, encontrado, reposto, capacidade).then((r) => {
      setEnviando(false);
      if (r.tipo !== 'confirmada') return setFalha(textoDaFalha(r));
      if (r.dados.divergencia)
        ir({ t: 'diferenca', missaoId: m.missaoId, r: r.dados, nome: i.nome });
      else ir({ t: 'fila', missaoId: m.missaoId });
    });
  };
  return (
    <>
      <header className="gb-fluxo-cab">
        <button type="button" className="gb-voltar" aria-label="Voltar" onClick={aoVoltar}>
          <Icone nome="arrow-l" tamanho={18} traco={1.8} />
        </button>
        <div>
          <b>Reposição</b>
          <em>
            Produto {pos} de {f?.itens.length ?? 0}
          </em>
        </div>
        <span style={{ flex: 1 }} />
        <b className="gb-pct">{pct}%</b>
        <div className="gb-fluxo-barra">
          <i style={{ width: `${String(pct)}%` }} />
        </div>
      </header>
      <div className="gb-corpo">
        {!f && falhou ? (
          <FalhaAoCarregar oQue="este produto" aoTentar={carregar} aoVoltar={aoVoltar} />
        ) : f && !i ? (
          <div className="gb-cartao gb-erro">
            <b>Este produto não está mais na sua fila</b>
            <p>A lista de reposição mudou. Volte para a fila e siga pelo próximo.</p>
            <button type="button" className="gb-botao gb-botao-linha" onClick={aoVoltar}>
              <Icone nome="arrow-l" tamanho={15} traco={1.8} /> Voltar à fila
            </button>
          </div>
        ) : !i ? (
          <div className="gb-esqueleto" aria-busy="true">
            <i />
          </div>
        ) : (
          <>
            <div className="gb-precisamos">
              <em>PRECISAMOS REPOR</em>
              <div>
                <strong>{i.repor}</strong>
                <span>unidades</span>
              </div>
              <div className="gb-tres gb-tres-escuro">
                <div>
                  <em>Prateleira</em>
                  <b>{i.prateleira}</b>
                </div>
                <div>
                  <em>Alvo</em>
                  <b>{i.alvo}</b>
                </div>
                <div>
                  <em>Estoque</em>
                  <b>{i.estoque ?? '—'}</b>
                </div>
              </div>
            </div>
            <FichaDoProduto item={i} />
            {modo === 'escolha' ? (
              <>
                <button
                  type="button"
                  className="gb-opcao gb-opcao-verde"
                  disabled={enviando}
                  onClick={() => registrar(i.repor)}
                >
                  <Icone nome="check" tamanho={18} traco={2} />
                  <div>
                    <b>Achei as {i.repor} · vou repor</b>
                    <em>
                      encontrado {i.repor} · reposto {i.repor}
                    </em>
                  </div>
                </button>
                {i.alvo > 1 && (
                  <button
                    type="button"
                    className="gb-opcao gb-opcao-azul"
                    disabled={enviando}
                    onClick={() => {
                      setCabem(null);
                      setModo('cabe');
                    }}
                  >
                    <Icone nome="box" tamanho={18} traco={1.8} />
                    <div>
                      <b>Coloquei só o que cabe na prateleira</b>
                      <em>produto grande: você diz quantos cabem</em>
                    </div>
                  </button>
                )}
                {i.repor > 1 && (
                  <button
                    type="button"
                    className="gb-opcao gb-opcao-ambar"
                    disabled={enviando}
                    onClick={() => setModo('parte')}
                  >
                    <Icone nome="warn" tamanho={18} traco={1.8} />
                    <div>
                      <b>Achei uma parte</b>
                      <em>você informa quantas achou</em>
                    </div>
                  </button>
                )}
                <button
                  type="button"
                  className="gb-opcao gb-opcao-vermelha"
                  disabled={enviando}
                  onClick={() => registrar(0)}
                >
                  <div>
                    <b>Não achei nenhuma</b>
                    <em>nada disponível no estoque</em>
                  </div>
                </button>
                <p className="gb-nota gb-centro">
                  Toque na opção que corresponde ao que você encontrou.
                </p>
                <button
                  type="button"
                  className="gb-link gb-pular"
                  disabled={enviando || pulando}
                  onClick={pular}
                  aria-label="Pular este produto: fica para o fim da fila"
                >
                  <Icone nome="clock" tamanho={14} traco={1.7} />{' '}
                  {pulando ? 'Pulando…' : 'Pular produto · fica para o fim'}
                </button>
              </>
            ) : modo === 'cabe' ? (
              <>
                <div className="gb-faixa gb-faixa-ambar gb-faixa-fina">
                  <Icone nome="warn" tamanho={15} traco={1.8} />
                  <b>Esse produto é grande?</b>
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="gb-link gb-link-peq"
                    onClick={() => setModo('escolha')}
                  >
                    Trocar
                  </button>
                </div>
                <div className="gb-cartao gb-contador">
                  <b>Quantos cabem na prateleira, no total?</b>
                  <p className="gb-nota gb-centro">
                    O normal é {i.alvo}. Responda com cuidado: o gestor confere cada produto que
                    alguém disse que não cabe {i.alvo}.
                  </p>
                  <div className="gb-rapidos">
                    {Array.from({ length: i.alvo - 1 }, (_, k) => k + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={cabem === n ? 'gb-rapido-sel' : ''}
                        onClick={() => setCabem(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {cabem !== null && (
                    <p className="gb-nota gb-centro">
                      {Math.max(0, cabem - i.prateleira) === 0
                        ? `Já tem ${String(i.prateleira)} na prateleira: não cabe mais nenhum.`
                        : `Tem ${String(i.prateleira)} na prateleira: reponha ${String(Math.min(i.repor, Math.max(0, cabem - i.prateleira)))}.`}
                    </p>
                  )}
                </div>
                <div className="gb-fixo">
                  <button
                    type="button"
                    className="gb-botao"
                    disabled={enviando || cabem === null}
                    onClick={() =>
                      cabem !== null &&
                      registrar(
                        i.repor,
                        Math.min(i.repor, Math.max(0, cabem - i.prateleira)),
                        cabem,
                      )
                    }
                  >
                    {cabem === null
                      ? 'Escolha quantos cabem'
                      : `Cabem ${String(cabem)} · repus ${String(Math.min(i.repor, Math.max(0, cabem - i.prateleira)))}`}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="gb-faixa gb-faixa-ambar gb-faixa-fina">
                  <Icone nome="warn" tamanho={15} traco={1.8} />
                  <b>Você achou uma parte</b>
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="gb-link gb-link-peq"
                    onClick={() => setModo('escolha')}
                  >
                    Trocar
                  </button>
                </div>
                <div className="gb-cartao gb-contador">
                  <b>Quantas unidades você encontrou no estoque?</b>
                  <div className="gb-stepper">
                    <button
                      type="button"
                      aria-label="Menos uma"
                      onClick={() => setAchou((q) => Math.max(1, q - 1))}
                    >
                      <Icone nome="minus" tamanho={20} traco={1.8} />
                    </button>
                    <output aria-live="polite">{achou}</output>
                    <button
                      type="button"
                      aria-label="Mais uma"
                      onClick={() => setAchou((q) => Math.min(i.repor - 1, q + 1))}
                    >
                      <Icone nome="plus" tamanho={20} traco={1.8} />
                    </button>
                  </div>
                  <div className="gb-rapidos">
                    {Array.from({ length: Math.min(3, i.repor - 1) }, (_, k) => k + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={achou === n ? 'gb-rapido-sel' : ''}
                        onClick={() => setAchou(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="gb-nota gb-centro">
                    De 1 a {i.repor - 1} — achar {i.repor} é &quot;achei tudo&quot; e 0 é &quot;não
                    achei nenhuma&quot;.
                  </p>
                </div>
                <div className="gb-fixo">
                  <button
                    type="button"
                    className="gb-botao"
                    disabled={enviando}
                    onClick={() => registrar(achou)}
                  >
                    Confirmar {achou} {achou === 1 ? 'encontrada' : 'encontradas'}
                  </button>
                </div>
              </>
            )}
            {falha && (
              <p className="gb-falha" role="alert">
                {falha}
              </p>
            )}
            <a className="gb-problema" href="#/conta">
              <Icone nome="pin" tamanho={13} traco={1.6} /> Informar problema neste item
            </a>
          </>
        )}
      </div>
    </>
  );
}

/**
 * EDITAR O GIRO DE HOJE (dono, 13/10/2026): antes de finalizar, a lista do que ele lançou. Ele só
 * corrige QUANTO TINHA NA PRATELEIRA e QUANTO ACHOU NO ESTOQUE. O pedido do sistema e o estoque do
 * HOS aparecem com cadeado. Quem decide o que a correção muda (pedido, caso aberto) é o servidor.
 */
function EditarGiroDeHoje({
  sessao,
  m,
  aoVoltar,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  aoVoltar: () => void;
}) {
  const [l, setL] = useState<{ editavel: boolean; itens: ItemParaEditar[] } | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const carregar = useCallback(() => {
    setFalhou(false);
    void lerParaEditar(m.missaoId).then((r) => {
      if (r.ok) setL(r.dados);
      else setFalhou(true);
    });
  }, [m.missaoId]);
  useEffect(carregar, [carregar]);
  return (
    <>
      <header className="gb-fluxo-cab">
        <button type="button" className="gb-voltar" aria-label="Voltar" onClick={aoVoltar}>
          <Icone nome="arrow-l" tamanho={18} traco={1.8} />
        </button>
        <div>
          <b>Editar giro de hoje</b>
          <em className="gb-mono">
            {m.setor} · {bN(m.numero)} · {m.faixa}
          </em>
        </div>
      </header>
      <div className="gb-corpo">
        {!l && falhou ? (
          <FalhaAoCarregar oQue="os seus produtos" aoTentar={carregar} aoVoltar={aoVoltar} />
        ) : !l ? (
          <div className="gb-esqueleto" aria-busy="true">
            <i />
          </div>
        ) : (
          <>
            <p className="gb-nota">
              Corrija só o que você digitou errado: <b>prateleira</b> e <b>achei no estoque</b>. O
              pedido do sistema e o estoque do HOS não mudam por aqui.
            </p>
            {aviso && (
              <div className="gb-cartao gb-aviso" role="status">
                <Icone nome="check" tamanho={15} traco={2} />
                <p>{aviso}</p>
              </div>
            )}
            {!l.itens.length && (
              <div className="gb-cartao">
                <p>Nenhum produto contado ainda neste bloco.</p>
              </div>
            )}
            {l.itens.map((i) => (
              <ItemEditavel
                key={i.id}
                sessao={sessao}
                i={i}
                aberto={aberto === i.id}
                aoAbrir={() => {
                  setAviso(null);
                  setAberto(aberto === i.id ? null : i.id);
                }}
                aoSalvar={(texto) => {
                  setAberto(null);
                  setAviso(texto);
                  carregar();
                }}
              />
            ))}
            <div className="gb-fixo">
              <button type="button" className="gb-botao" onClick={aoVoltar}>
                <Icone nome="check" tamanho={16} traco={2} /> Pronto, voltar
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ItemEditavel({
  sessao,
  i,
  aberto,
  aoAbrir,
  aoSalvar,
}: {
  sessao: Sessao;
  i: ItemParaEditar;
  aberto: boolean;
  aoAbrir: () => void;
  aoSalvar: (texto: string) => void;
}) {
  const [prat, setPrat] = useState(i.prateleira);
  const [achou, setAchou] = useState(i.encontrado ?? 0);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  useEffect(() => {
    setPrat(i.prateleira);
    setAchou(i.encontrado ?? 0);
    setFalha(null);
  }, [aberto, i.prateleira, i.encontrado]);
  const mudou = prat !== i.prateleira || (i.encontrado !== null && achou !== i.encontrado);
  const salvar = () => {
    if (!mudou || enviando) return;
    setEnviando(true);
    setFalha(null);
    const d: { prateleira?: number; encontrado?: number } = {};
    if (prat !== i.prateleira) d.prateleira = prat;
    if (i.encontrado !== null && achou !== i.encontrado) d.encontrado = achou;
    void editarItem(sessao, i.id, d).then((r) => {
      setEnviando(false);
      if (r.tipo !== 'confirmada') return setFalha(textoDaFalha(r));
      const x = r.dados;
      const partes = [`${i.nome}: corrigido`];
      if (x.pedido !== i.pedido && x.pedido !== null)
        partes.push(`o sistema agora pede repor ${String(x.pedido)}`);
      if (x.casosCorrigidos && !x.falta) partes.push('a diferença foi fechada como corrigida');
      else if (x.falta) partes.push(`ainda faltam ${String(x.falta)} (vai para análise)`);
      aoSalvar(`${partes.join(' · ')}.`);
    });
  };
  const contador = (rotulo: string, valor: number, mudar: (n: number) => void, max = 9999) => (
    <div className="gb-editar-campo">
      <b>{rotulo}</b>
      <div className="gb-stepper">
        <button
          type="button"
          aria-label={`${rotulo}: menos uma`}
          onClick={() => mudar(Math.max(0, valor - 1))}
        >
          <Icone nome="minus" tamanho={20} traco={1.8} />
        </button>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={max}
          aria-label={rotulo}
          value={valor}
          onChange={(e) => {
            const n = Math.floor(Number(e.target.value));
            mudar(Number.isFinite(n) ? Math.min(max, Math.max(0, n)) : 0);
          }}
        />
        <button
          type="button"
          aria-label={`${rotulo}: mais uma`}
          onClick={() => mudar(Math.min(max, valor + 1))}
        >
          <Icone nome="plus" tamanho={20} traco={1.8} />
        </button>
      </div>
    </div>
  );
  return (
    <div className={`gb-cartao gb-editar-item ${aberto ? 'gb-editar-aberto' : ''}`}>
      <div className="gb-editar-topo">
        <div>
          <b>{i.nome}</b>
          <em className="gb-mono">
            {i.codigo ? `cód. ${i.codigo}` : 'sem código'}
            {i.editado ? ' · editado' : ''}
          </em>
        </div>
        {i.podeEditarPrateleira && (
          <button
            type="button"
            className="gb-botao gb-botao-peq gb-botao-linha"
            onClick={aoAbrir}
            aria-expanded={aberto}
          >
            {aberto ? 'Fechar' : 'Editar'}
          </button>
        )}
      </div>
      <div className="gb-quatro">
        <div className="gb-quatro-edita">
          <em>Prateleira</em>
          <b>{i.naoLocalizado && i.prateleira === 0 ? 'não achou' : i.prateleira}</b>
        </div>
        <div className="gb-quatro-edita">
          <em>Achei no estoque</em>
          <b>{i.encontrado ?? '—'}</b>
        </div>
        <div className="gb-quatro-trava" title="O sistema calcula: não dá para editar">
          <em>
            <Icone nome="lock" tamanho={11} traco={1.8} /> Pedido repor
          </em>
          <b>{i.pedido ?? 'aguarda'}</b>
        </div>
        <div
          className="gb-quatro-trava"
          title={i.hosLidoEm ? `Lido às ${hora(i.hosLidoEm)}` : 'Sem leitura do HOS'}
        >
          <em>
            <Icone nome="lock" tamanho={11} traco={1.8} /> HOS agora
          </em>
          <b>{i.hosAgora ?? '—'}</b>
        </div>
      </div>
      {i.travado && <p className="gb-nota">{i.travado}</p>}
      {aberto && i.podeEditarPrateleira && (
        <div className="gb-editar-form">
          {contador('Quantas tinha na prateleira', prat, setPrat)}
          {i.podeEditarEncontrado &&
            contador('Quantas achei no estoque', achou, setAchou, i.pedido ?? 9999)}
          {i.encontrado !== null && prat !== i.prateleira && (
            <p className="gb-nota">
              Este produto já foi buscado no estoque: o pedido do sistema não muda.
            </p>
          )}
          {falha && (
            <p className="gb-falha" role="alert">
              {falha}
            </p>
          )}
          <button type="button" className="gb-botao" disabled={!mudou || enviando} onClick={salvar}>
            {enviando ? 'Salvando…' : mudou ? 'Salvar correção' : 'Nada mudou'}
          </button>
        </div>
      )}
    </div>
  );
}

function DiferencaRegistrada({
  m,
  tela,
  ir,
}: {
  m: MissaoDoBalconista;
  tela: Extract<Tela, { t: 'diferenca' }>;
  ir: (t: Tela) => void;
}) {
  const r = tela.r;
  return (
    <>
      <header className="gb-fluxo-cab">
        <div>
          <b>Reposição</b>
          <em>{tela.nome}</em>
        </div>
      </header>
      <div className="gb-corpo">
        <div className="gb-cartao">
          <div className="gb-alerta-topo gb-ambar-bg gb-esq">
            <b>
              <Icone nome="warn" tamanho={16} traco={1.8} /> Diferença registrada
            </b>
            <p>
              O sistema esperava disponibilidade para {r.pedido} unidades, e foram encontradas{' '}
              {r.encontrado}.
            </p>
          </div>
          <ul className="gb-resumo">
            <li>
              Esperado para reposição <b>{r.pedido}</b>
            </li>
            <li>
              Encontrado no estoque <b>{r.encontrado}</b>
            </li>
            <li>
              Reposto na prateleira <b>{r.reposto}</b>
            </li>
            <li>
              Prateleira agora{' '}
              <b>
                {r.prateleiraAgora} <em className="gb-nota">de {r.alvo}</em>
              </b>
            </li>
          </ul>
        </div>
        <div className="gb-cartao gb-info">
          <Icone nome="shield" tamanho={15} traco={1.6} />
          <p>
            <b>Enviado para análise.</b> Pode ter havido venda depois do último saldo, movimentação,
            produto em outro local ou ajuste de sistema. Você não precisa esperar a resposta.
          </p>
        </div>
        <div className="gb-cartao gb-info">
          <Icone nome="filter" tamanho={15} traco={1.6} />
          <p>
            Você registrou o que viu. Nenhuma tela do Giro chama isso de erro, e ninguém é comparado
            enquanto trabalha.
          </p>
        </div>
        <div className="gb-fixo">
          <button
            type="button"
            className="gb-botao"
            onClick={() => ir({ t: 'fila', missaoId: m.missaoId })}
          >
            Próximo item
          </button>
        </div>
      </div>
    </>
  );
}

function GiroConcluido({
  tela,
  bloco,
  ciclo,
  aoVoltar,
}: {
  tela: Extract<Tela, { t: 'fim' }>;
  bloco: MeuBloco | undefined;
  ciclo: MeuGiro['ciclos'][number] | undefined;
  aoVoltar: () => void;
}) {
  return (
    <div className="gb-cartao gb-fim">
      <div className="gb-fim-topo">
        <span className="gb-check-grande gb-check-maior">
          <Icone nome="check" tamanho={26} traco={2.4} />
        </span>
        <b>Giro concluído</b>
        {bloco && (
          <em className="gb-mono">
            {bloco.setor} · {bN(bloco.numero)} · {bloco.faixa}
          </em>
        )}
      </div>
      <ul className="gb-resumo">
        <li>
          Produtos conferidos <b>{tela.resumo.conferidos}</b>
        </li>
        <li>
          Reposições <b>{tela.repostos}</b>
        </li>
        <li>
          Unidades repostas <b>{tela.unidades}</b>
        </li>
        <li>
          Diferenças para análise <b>{tela.diferencas}</b>
        </li>
      </ul>
      <ComoFoiMeuGiro />
      {ciclo && (
        <p className="gb-nota gb-caixa">
          Este bloco foi registrado no seu ciclo — {ciclo.feitos} de {ciclo.total} em {ciclo.setor}.
        </p>
      )}
      {/* "Liberado" é a resposta do servidor ao finalizar — nunca suposição da tela. */}
      {tela.liberado ? (
        <>
          <div className="gb-faixa gb-faixa-verde gb-faixa-fina">
            <Icone nome="check" tamanho={15} traco={2} />
            <b>Atividades do InovaPay liberadas</b>
          </div>
          <a className="gb-botao" href="#/inicio" onClick={entrarNoInovaPay}>
            Entrar no InovaPay
          </a>
          <p className="gb-nota gb-centro">
            Vitaminas, pré-vencidos e paradão ficam disponíveis a partir de agora
          </p>
        </>
      ) : (
        <>
          <div className="gb-faixa gb-faixa-ambar gb-faixa-fina">
            <Icone nome="warn" tamanho={15} traco={1.8} />
            <b>O InovaPay ainda não foi liberado</b>
          </div>
          <p className="gb-nota gb-centro">
            Este bloco foi registrado, mas ainda há pendência no seu Giro de hoje. Volte ao Giro
            para ver o que falta.
          </p>
          <button type="button" className="gb-botao" onClick={aoVoltar}>
            Voltar ao Giro
          </button>
        </>
      )}
    </div>
  );
}

/* ---------- ocorrência do corredor (§37 do 1.0) ---------- */

/**
 * "Algo errado na prateleira?" — registrar NÃO mexe na contagem nem prende a missão. Sem este
 * canal a pessoa "marca" o problema contando errado de propósito, e o dado da auditoria estraga.
 */
function RegistrarOcorrencia({
  sessao,
  missaoId,
  itemId,
}: {
  sessao: Sessao;
  missaoId: string;
  itemId: string | null;
}) {
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState<TipoDeOcorrencia | null>(null);
  const [valor, setValor] = useState('');
  const [detalhe, setDetalhe] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [registradas, setRegistradas] = useState<string[]>([]);
  const def = TIPOS_DE_OCORRENCIA.find((t) => t.tipo === tipo);
  const pronto = !!tipo && (tipo !== 'OUTRO' || detalhe.trim().length > 0);
  const enviar = () => {
    if (!tipo || !def) return;
    setEnviando(true);
    setFalha(null);
    void registrarOcorrencia(sessao, missaoId, { itemId, tipo, detalhe, valor }).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') {
        setRegistradas((x) => [...x, def.rotulo]);
        setAberto(false);
        setTipo(null);
        setValor('');
        setDetalhe('');
      } else setFalha(textoDaFalha(r));
    });
  };
  return (
    <div className="gb-ocorrencia">
      {registradas.map((r, k) => (
        <p key={k} className="gb-ocorrencia-ok" role="status">
          <Icone nome="check" tamanho={13} traco={2} /> Ocorrência registrada: {r}
        </p>
      ))}
      {!aberto ? (
        <button type="button" className="gb-link" onClick={() => setAberto(true)}>
          <Icone nome="warn" tamanho={14} traco={1.7} /> Algo errado na prateleira?
        </button>
      ) : (
        <div className="gb-cartao gb-ocorrencia-form">
          <b>O que você encontrou?</b>
          <em>Não muda a contagem — vai para o administrador resolver.</em>
          <div className="gb-ocorrencia-tipos" role="radiogroup" aria-label="Tipo de ocorrência">
            {TIPOS_DE_OCORRENCIA.map((t) => (
              <button
                key={t.tipo}
                type="button"
                role="radio"
                aria-checked={tipo === t.tipo}
                className={tipo === t.tipo ? 'gb-rapido-sel' : ''}
                onClick={() => setTipo(t.tipo)}
              >
                {t.rotulo}
              </button>
            ))}
          </div>
          {def?.valor && (
            <label>
              <span>{def.valor}</span>
              <input value={valor} maxLength={60} onChange={(e) => setValor(e.target.value)} />
            </label>
          )}
          {tipo && (
            <label>
              <span>{tipo === 'OUTRO' ? 'Descreva o que aconteceu' : 'Detalhe (opcional)'}</span>
              <textarea
                rows={2}
                maxLength={500}
                value={detalhe}
                onChange={(e) => setDetalhe(e.target.value)}
              />
            </label>
          )}
          {falha && (
            <p className="gb-falha" role="alert">
              {falha}
            </p>
          )}
          <div className="gb-ocorrencia-acoes">
            <button
              type="button"
              className="gb-botao"
              disabled={!pronto || enviando}
              onClick={enviar}
            >
              {enviando ? 'Registrando…' : 'Registrar ocorrência'}
            </button>
            <button type="button" className="gb-link" onClick={() => setAberto(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- pausa (§13 do 1.0) ---------- */

function Pausar({
  sessao,
  missaoId,
  aoCancelar,
  aoPausar,
}: {
  sessao: Sessao;
  missaoId: string;
  aoCancelar: () => void;
  aoPausar: () => void;
}) {
  const [tipo, setTipo] = useState<TipoDePausa | null>(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const pronto = !!tipo && (tipo !== 'OUTRO' || motivo.trim().length > 0);
  const enviar = () => {
    if (!tipo) return;
    setEnviando(true);
    setFalha(null);
    void pausarMissao(sessao, missaoId, tipo, motivo).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoPausar();
      else setFalha(textoDaFalha(r));
    });
  };
  return (
    <>
      <header className="gb-fluxo-cab">
        <button type="button" className="gb-voltar" aria-label="Voltar" onClick={aoCancelar}>
          <Icone nome="arrow-l" tamanho={18} traco={1.8} />
        </button>
        <div>
          <b>Pausar o Giro</b>
          <em>O tempo parado não conta como tempo da missão</em>
        </div>
      </header>
      <div className="gb-corpo">
        <div className="gb-cartao gb-ocorrencia-form">
          <b>Por que você vai parar?</b>
          <div className="gb-ocorrencia-tipos" role="radiogroup" aria-label="Motivo da pausa">
            {TIPOS_DE_PAUSA.map((t) => (
              <button
                key={t.tipo}
                type="button"
                role="radio"
                aria-checked={tipo === t.tipo}
                className={tipo === t.tipo ? 'gb-rapido-sel' : ''}
                onClick={() => setTipo(t.tipo)}
              >
                {t.rotulo}
              </button>
            ))}
          </div>
          {tipo === 'OUTRO' && (
            <label>
              <span>Diga o motivo</span>
              <input value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} />
            </label>
          )}
        </div>
        {falha && (
          <p className="gb-falha" role="alert">
            {falha}
          </p>
        )}
        <div className="gb-fixo">
          <button
            type="button"
            className="gb-botao"
            disabled={!pronto || enviando}
            onClick={enviar}
          >
            {enviando ? 'Pausando…' : 'Pausar agora'}
          </button>
          <button type="button" className="gb-link" onClick={aoCancelar}>
            Continuar o Giro
          </button>
        </div>
      </div>
    </>
  );
}

function EmPausa({
  sessao,
  m,
  aoVoltar,
  aoRetomar,
}: {
  sessao: Sessao;
  m: MissaoDoBalconista;
  aoVoltar: () => void;
  aoRetomar: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const pausa = m.pausa;
  if (!pausa) return null;
  const min = Math.max(0, Math.round((agora - Date.parse(pausa.desde)) / 60000));
  const rotulo = TIPOS_DE_PAUSA.find((t) => t.tipo === pausa.tipo)?.rotulo ?? pausa.tipo;
  const retomar = () => {
    setEnviando(true);
    setFalha(null);
    void retomarMissao(sessao, m.missaoId).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoRetomar();
      else setFalha(textoDaFalha(r));
    });
  };
  return (
    <>
      <header className="gb-fluxo-cab">
        <button type="button" className="gb-voltar" aria-label="Voltar" onClick={aoVoltar}>
          <Icone nome="arrow-l" tamanho={18} traco={1.8} />
        </button>
        <div>
          <span className="gb-mono">{`${m.setor} · ${bN(m.numero)} · ${m.faixa}`}</span>
          <em>Giro em pausa</em>
        </div>
      </header>
      <div className="gb-corpo">
        <div className="gb-cartao gb-pausa">
          <Icone nome="clock" tamanho={28} traco={1.6} />
          <b>Em pausa · {rotulo}</b>
          <p>
            desde {hora(pausa.desde)} · {min < 1 ? 'agora há pouco' : `há ${String(min)} min`}
            {pausa.motivo ? ` · ${pausa.motivo}` : ''}
          </p>
          <em>
            O tempo parado não conta como tempo da missão. Quando voltar, toque em retomar — a
            contagem continua de onde parou.
          </em>
        </div>
        {falha && (
          <p className="gb-falha" role="alert">
            {falha}
          </p>
        )}
        <div className="gb-fixo">
          <button type="button" className="gb-botao" disabled={enviando} onClick={retomar}>
            {enviando ? 'Retomando…' : 'Retomar o Giro'}
          </button>
        </div>
      </div>
    </>
  );
}
